'use client';

import { useEffect, useMemo, useRef, useState, type JSX, type ReactNode } from 'react';
import { ArrowRight, ArrowRightLeft } from 'lucide-react';
import {
  formatRideCapacityLabel,
  isRideCompositionFeasible,
  PrestationPaymentType,
  ProPaymentStatus,
  RIDE_MAX_VEHICLES_PER_BOOKING,
  rideGroupFitsSingleVehicle,
  RideTripLeg,
  rideLuggageDisplayParts,
  sumRideCompositionBounds,
} from '@cfixe-monorepo/shared';
import type {
  AddressObject,
  RideCheckoutResponseObject,
  RideCompositionBoundsLike,
  RideCompositionItem,
  RidePriceCalculatorResponseObject,
  RidePublicConfigResponseObject,
  RidePublicPrestationObject,
  RidePublicVehicleObject,
  RideQuoteVehicleLineObject,
  RideVehicleCompositionItemObject,
} from '@cfixe-monorepo/shared';
import { CustomerInfoFields } from '../../booking/components/shared/CustomerInfoFields';
import { DynamicFormFields } from '../../booking/components/shared/DynamicFormFields';
import { FormField } from '../../booking/components/shared/FormField';
import { useCustomFieldAnswers } from '../../booking/components/shared/useCustomFieldAnswers';
import type { BookingCustomField } from '../../booking/types';
import {
  NO_BOOKED_PLACE_IDS,
  answersForFields,
  mapPublicCustomField,
  serializeCustomFieldAnswers,
} from '../../booking/utils/custom-fields';
import { QuantitySelector } from '../../booking/components/shared/QuantitySelector';
import { RichDescription } from '../../booking/components/shared/RichDescription';
import { PaymentReassurance } from '../../booking/components/shared/PaymentReassurance';
import {
  TunnelSummary,
  type TunnelSummaryContextRow,
  type TunnelSummaryCta,
  type TunnelSummaryItem,
  type TunnelSummaryPaymentRow,
  type TunnelSummaryProps,
} from '../../booking/components/shared/TunnelSummary';
import { TunnelFooterBar } from '../../booking/components/shared/TunnelFooterBar';
import { useStepTransitionFocus } from '../../booking/components/shared/useStepTransitionFocus';
import { useScrollToError } from '../../booking/components/shared/useScrollToError';
import { GiftCardPaymentSection } from '../../gift-card/components/GiftCardPaymentSection';
import { BookingManageView } from '../../booking/components/manage/BookingManageView';
import '../../booking/styles/booking-tunnel.scss';
import { RideProgress } from './RideProgress';
import { BookingOutcome } from '../../booking/components/shared/BookingOutcome';
import { RedirectPaymentNotice } from '../../booking/components/shared/RedirectPaymentNotice';
import { REDIRECT_RETURN_PARAM, hasRedirectReturn } from '../../pro-payment/redirect-return';
import { redirectReturnMessage, useRedirectReturn } from '../../pro-payment/useRedirectReturn';
import { PaymentTimeline } from '../../booking/components/shared/PaymentTimeline';
import { openNativeDatePicker } from '../../booking/utils/native-picker';
import { isBuilderCutError } from '../../website/functions/builder-events';
import { RideAddressField } from './RideAddressField';
import { RideVehicleCard } from './RideVehicleCard';
import {
  claimOpenRidePayment,
  clearRidePaymentSnapshot,
  loadOpenRidePayment,
  loadRidePaymentSnapshot,
  releaseOpenRidePayment,
  saveRidePaymentSnapshot,
  type RidePaymentSnapshot,
} from './ride-payment-snapshot';
import { DEFAULT_RIDE_LABELS, type RideInitialTrip, type RideLabels, type RideTypeSelection } from '../types';
import type { RideCallbacks } from '../api/create-ride-callbacks';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/* Plafonds des compteurs passagers / bagages.
   - `DEFAULT_*` : plafonds MONO historiques, appliqués tels quels tant que le multi-véhicules est
     inactif (réglage du pro coupé : rendu strictement mono) ;
   - `MAX_*` : plafond dur du tunnel une fois le multi actif — le tunnel ne propose jamais plus,
     même si la flotte annoncée par la config permettrait davantage. */
const RIDE_TUNNEL_DEFAULT_MAX_PASSENGERS = 16;
const RIDE_TUNNEL_DEFAULT_MAX_LUGGAGE = 20;
const RIDE_TUNNEL_MAX_PASSENGERS = 60;
const RIDE_TUNNEL_MAX_LUGGAGE = 60;

type TripType = 'ONE_WAY' | 'ROUND_TRIP';
// Parcours en 2 étapes (miroir gift-card) : trajet + estimation → coordonnées (puis paiement inline).
type Step = 'trip' | 'quote' | 'details';
const STEPS: Step[] = ['trip', 'quote', 'details'];

// « Maintenant » au format d'un `datetime-local` (YYYY-MM-DDTHH:mm) dans le fuseau `timezone`. La
// prise en charge est une heure MURALE interprétée dans le fuseau du PRO (comme au backend) : on la
// compare donc à « maintenant » dans CE fuseau, pas dans celui du navigateur (sinon faux refus/
// passages dès que le client réserve depuis un autre fuseau). Les deux chaînes ISO se comparent
// chronologiquement ; hourCycle h23 → 00-23 (évite « 24:00 » à minuit).
const nowWallTimeInTimezone = (timezone: string): string => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date());
  const get = (type: string): string => parts.find((p): boolean => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
};

export interface RideTunnelProps {
  callbacks: RideCallbacks;
  professionalName: string;
  stripePublishableKey: string;
  /** Libellés (défauts fr) — même pattern que BookingLabels/DEFAULT_LABELS. */
  labels?: Partial<RideLabels>;
  /**
   * Mini-carte du trajet, injectée par l'hôte (le loader Google Maps vit dans frontend-web).
   * Props primitives : les deps d'effet comparent par valeur (évite les appels Directions facturés).
   */
  renderRouteMap?: (coords: {
    pickupLat: number;
    pickupLng: number;
    dropoffLat: number;
    dropoffLng: number;
    /** Polyline encodé de l'itinéraire retenu pour le prix (tracé exact, sans DirectionsService). */
    encodedPolyline?: string;
  }) => ReactNode;
  /** Clé d'erreur API (`API_ERRORS`) → message localisé ; défaut = message générique. */
  resolveApiErrorLabel?: (errorKey: string) => string | undefined;
  /** Locale BCP-47 pour le formatage client (téléphone, date du récap). Défaut fr-FR. */
  locale?: string;
  /** Rendu sur la ligne du titre de la première étape, aligné à droite (ex. sélecteur de mode /book). */
  headerAccessory?: ReactNode;
  /**
   * Formule présélectionnée (deep-link `?rp=<id>` depuis une carte « Course » d'un site public).
   * Validée contre les formules du pro au chargement — sinon on retombe sur la première.
   */
  initialPrestationId?: number;
  /**
   * Trajet par défaut (carte « Course » du tunnel classique — onSelectRidePrestation) : formule
   * présélectionnée + adresses préremplies, appliqués à l'init ET quand la RÉFÉRENCE de la prop
   * change (nouvelle carte cliquée pendant que le tunnel est monté). Une adresse `*_locked` est
   * affichée en lecture seule (champ désactivé + mention « Adresse fixe »).
   */
  initialTrip?: RideInitialTrip;
  /**
   * `false` = laisser le retour de paiement Stripe de la page (`REDIRECT_RETURN_PARAM.RIDE`) à un
   * autre tunnel course : l'URL n'est ni lue ni nettoyée. Le widget embarqué ne le passe à `true`
   * que pour l'instance qui a réclamé ce retour (miroir de `BookingTunnel`). Défaut `true`.
   */
  handleRedirectReturn?: boolean;
  /**
   * Base URL des drapeaux du champ téléphone (widget embarqué chez un tiers : servis depuis l'hôte
   * du widget, pas l'origine de la page). Défaut : `DEFAULT_FLAG_BASE_URL` (même origine).
   */
  flagBaseUrl?: string;
}

/** Date/heure du récap, formatée client (donnée saisie par le client — jamais renvoyée par le back). */
function formatLocalDatetime(local: string, locale: string): string {
  const date = new Date(local);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

/** Cohérence retour/aller (deux `datetime-local` du même fuseau mural) : vide = pas de conflit. */
function isReturnAfterPickup(returnLocal: string, pickupLocal: string): boolean {
  if (!returnLocal || !pickupLocal) return true;
  return new Date(returnLocal) > new Date(pickupLocal);
}

/**
 * Bornes de capacité de la sélection courante du volet « Type de course » — la MÊME sémantique
 * que validateTrip/le backend : une borne absente = non contraint sur cet axe. En « peu importe »,
 * l'agrégat du catalogue (max des maxes) : refuser au-delà est sûr, AUCUN candidat ne peut accueillir.
 */
function aggregateCapacityBounds(
  items: Array<{
    pax_max?: number | null;
    small_luggage_max?: number | null;
    large_luggage_max?: number | null;
  }>,
): { paxMax: number; smallLuggageMax: number; largeLuggageMax: number } {
  if (items.length === 0)
    return {
      paxMax: Number.MAX_SAFE_INTEGER,
      smallLuggageMax: Number.MAX_SAFE_INTEGER,
      largeLuggageMax: Number.MAX_SAFE_INTEGER,
    };
  const axis = (values: Array<number | null | undefined>): number =>
    values.some((v): boolean => v == null)
      ? Number.MAX_SAFE_INTEGER
      : Math.max(...values.map((v): number => v as number));
  return {
    paxMax: axis(items.map((item): number | null | undefined => item.pax_max)),
    smallLuggageMax: axis(items.map((item): number | null | undefined => item.small_luggage_max)),
    largeLuggageMax: axis(items.map((item): number | null | undefined => item.large_luggage_max)),
  };
}

/**
 * Véhicules réservables de la config publique. `vehicles` est optionnel au contrat : un pro sans
 * flotte cotable n'en envoie aucun ⇒ tableau vide et le tunnel retombe intégralement sur le
 * sélecteur de formules. Le filtre reste défensif — la config vient d'une API publique.
 */
function readConfigVehicles(config: RidePublicConfigResponseObject | null): RidePublicVehicleObject[] {
  const vehicles = config?.vehicles;
  if (!Array.isArray(vehicles)) return [];
  return vehicles.filter(
    (v): boolean => v != null && typeof v.pro_resource_id === 'number' && typeof v.name === 'string',
  );
}

/**
 * Champs personnalisés (questionnaire) de la config publique, au format du tunnel. `custom_fields`
 * est optionnel au contrat (backend antérieur) ⇒ tableau vide, aucune section affichée.
 */
function readConfigCustomFields(config: RidePublicConfigResponseObject | null): BookingCustomField[] {
  const fields = config?.custom_fields;
  if (!Array.isArray(fields)) return [];
  return fields.map(mapPublicCustomField);
}

/** Formule de la sélection « Type de course » (vitrine/fallback) — « peu importe » / véhicule = aucune. */
function selectionPrestationIdOf(selection: RideTypeSelection): number | undefined {
  return typeof selection === 'object' && 'prestationId' in selection ? selection.prestationId : undefined;
}

/** Une unité de composition choisie par le client (classe de véhicule × quantité). */
type RideCompositionEntry = { vehicleId: number; quantity: number };

/**
 * Le multi-véhicules est-il ACTIF pour ce pro ? Réglage VTC du pro (`multi_vehicle_enabled`) ET
 * plafond autorisant au moins 2 véhicules. Faux ⇒ rendu ET requêtes strictement identiques à la
 * version mono : aucune interface de composition, aucun `vehicles` / `allow_split` envoyé.
 */
function isRideMultiVehicleActive(config: RidePublicConfigResponseObject | null): boolean {
  return config?.multi_vehicle_enabled === true && (config.max_vehicles_per_booking ?? 1) >= 2;
}

/** Composition explicite portée par la sélection (vide hors mode composition). */
function selectionCompositionOf(selection: RideTypeSelection): RideCompositionEntry[] {
  return typeof selection === 'object' && 'vehicles' in selection
    ? selection.vehicles.filter((item): boolean => item.quantity > 0)
    : [];
}

/** Sélection normalisée : une composition entièrement remise à zéro revient à « Automatique ». */
function toRideSelection(items: readonly RideCompositionEntry[]): RideTypeSelection {
  const kept = items.filter((item): boolean => item.quantity > 0);
  return kept.length === 0 ? 'any' : { vehicles: [...kept] };
}

const countCompositionVehicles = (items: readonly { quantity: number }[]): number =>
  items.reduce((total, item): number => total + item.quantity, 0);

/**
 * Composition dont la somme des MINIMUMS dépasse le groupe : c'est RETIRER un véhicule qu'il faut.
 * Deux causes, deux messages : plus de véhicules que de passagers (chaque véhicule en demande au
 * moins un), ou minimum d'une classe (pax_min / small_luggage_min / large_luggage_min) que le groupe
 * n'atteint pas — un seul Van « 5 places minimum » pour 3 passagers, par exemple.
 */
function compositionOverflowMessage(labels: RideLabels, vehicleCount: number, passengers: number): string {
  return vehicleCount > passengers
    ? labels.error_composition_too_many_vehicles.replace('{max}', String(passengers))
    : labels.error_composition_below_minimum;
}

/**
 * Message d'une composition explicite IRRÉALISABLE au sens du planificateur (isRideCompositionFeasible),
 * selon le SENS de l'écart — « Voir le prix » (validateTrip) et bandeau du pré-check disent la même
 * chose. Σ minimums au-delà du groupe (trop de véhicules, p. ex. après une baisse des passagers) →
 * en retirer ; sinon composition trop petite → en ajouter ou réduire le groupe.
 */
function compositionRefusalMessage(
  labels: RideLabels,
  items: readonly RideCompositionItem[],
  group: { passengers: number; smallLuggage: number; largeLuggage: number },
): string {
  const bounds = sumRideCompositionBounds(items);
  if (
    bounds.passenger_min > group.passengers ||
    bounds.small_luggage_min > group.smallLuggage ||
    bounds.large_luggage_min > group.largeLuggage
  ) {
    return compositionOverflowMessage(labels, countCompositionVehicles(items), group.passengers);
  }
  return labels.error_composition_capacity;
}

/**
 * Véhicules candidats du test « le groupe tient-il dans UN véhicule ? » (aiguillage mono / multi).
 * Formule imposée (vitrine) : les véhicules de la config qui la servent (`prestation_ids`). Ses
 * bornes AGRÉGÉES (min des mins, max des maxes) disaient « tient » pour un pool aux capacités
 * croisées — Van 8 places / 2 bagages + Berline 4 places / 6 bagages pour 6 passagers et 5 bagages :
 * le tunnel restait en mono et le devis refusait. Repli sur ces bornes agrégées quand aucun véhicule
 * ne rattache la formule (ancien backend, config en cache sans `prestation_ids`). Sans formule :
 * tous les véhicules réservables.
 */
function singleVehicleCandidates(
  prestation: RidePublicPrestationObject | undefined,
  vehicles: readonly RidePublicVehicleObject[],
): readonly RideCompositionBoundsLike[] {
  if (!prestation) return vehicles;
  const pool = vehicles.filter(
    (vehicle): boolean => vehicle.prestation_ids?.includes(prestation.prestation_id) === true,
  );
  return pool.length > 0 ? pool : [prestation];
}

/**
 * Champs multi-véhicules du payload, pour un pro dont le multi est ACTIF. Garde-fou du 23/09/2026 :
 * le chemin multi du backend (devis, pré-check, checkout transactionnel) n'est emprunté que par une
 * demande que le chemin mono ne sait pas servir. Tout ce qui se réserve déjà garde le code mono :
 * - composition d'UN seul véhicule → `vehicle_id`, c'est-à-dire un choix de véhicule classique ;
 * - composition de 2 véhicules ou plus → `vehicles` (le client l'a demandé explicitement) ;
 * - véhicule précis → aucun champ de plus (demande mono, jamais répartie sur d'autres classes) ;
 * - « Automatique » / vitrine → `allow_split` SEULEMENT si aucun véhicule ne porte le groupe seul.
 * Fonction pure de la saisie : devis, pré-check et checkout (y compris après un retour de
 * paiement) calculent donc toujours le même payload, donc la même empreinte de devis.
 */
function resolveMultiVehicleSelectionFields(args: {
  composition: readonly RideCompositionEntry[];
  selectedVehicleId: number | undefined;
  groupFitsSingleVehicle: boolean;
}): { vehicle_id?: number; vehicles?: RideVehicleCompositionItemObject[]; allow_split?: boolean } {
  if (args.composition.length > 0) {
    if (countCompositionVehicles(args.composition) === 1) return { vehicle_id: args.composition[0].vehicleId };
    return {
      vehicles: args.composition.map(
        (item): RideVehicleCompositionItemObject => ({ vehicle_id: item.vehicleId, quantity: item.quantity }),
      ),
    };
  }
  if (args.selectedVehicleId != null) return {};
  return args.groupFitsSingleVehicle ? {} : { allow_split: true };
}

/** Borne exploitable par un compteur : `Infinity` (axe non contraint) → pas de plafond. */
const finiteCapacityBound = (value: number): number => (Number.isFinite(value) ? value : Number.MAX_SAFE_INTEGER);

/** Règle d'affichage partagée (`rideLuggageDisplayParts`) ; le tunnel tait en plus un total à 0. */
function formatLuggageCounts(
  labels: RideLabels,
  total: number | undefined,
  small?: number | null,
  large?: number | null,
): string {
  const sizeLabel = (size: 'small' | 'large' | 'total'): string =>
    size === 'small' ? labels.small_luggage : size === 'large' ? labels.large_luggage : labels.luggage;
  return rideLuggageDisplayParts({ luggage_count: total, small_luggage_count: small, large_luggage_count: large })
    .filter((part): boolean => part.count > 0)
    .map((part): string => ` · 🧳 ${part.count} ${sizeLabel(part.size).toLowerCase()}`)
    .join('');
}

/** Lignes véhicule du devis — présentes seulement quand la composition en compte au moins deux. */
function quoteVehicleLines(quote: RidePriceCalculatorResponseObject | null): RideQuoteVehicleLineObject[] {
  if (!quote || (quote.vehicle_count ?? 1) < 2 || !Array.isArray(quote.vehicles)) return [];
  return quote.vehicles;
}

export function RideTunnel({
  callbacks,
  professionalName,
  stripePublishableKey,
  labels: labelOverrides,
  renderRouteMap,
  resolveApiErrorLabel,
  locale = 'fr-FR',
  headerAccessory,
  initialPrestationId,
  initialTrip,
  handleRedirectReturn = true,
  flagBaseUrl,
}: RideTunnelProps): JSX.Element {
  const labels = useMemo((): RideLabels => ({ ...DEFAULT_RIDE_LABELS, ...labelOverrides }), [labelOverrides]);
  // Libellés des tuiles véhicule (bouton « i » + modale de détail).
  const vehicleCardLabels = useMemo(
    (): { details: string; close: string; choose: string } => ({
      details: labels.vehicle_details,
      close: labels.close,
      choose: labels.choose,
    }),
    [labels.vehicle_details, labels.close, labels.choose],
  );

  const [config, setConfig] = useState<RidePublicConfigResponseObject | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<boolean>(false);

  const [step, setStep] = useState<Step>('trip');
  const [error, setError] = useState<string | null>(null);

  /* Trajet — préremplissable via `initialTrip` (carte « Course » du tunnel classique). */
  const [pickup, setPickup] = useState<AddressObject | undefined>(initialTrip?.pickup_address);
  const [dropoff, setDropoff] = useState<AddressObject | undefined>(initialTrip?.dropoff_address);
  // Trajet par défaut de la formule vitrine résolu depuis la CONFIG publique (arrivée deep-link
  // `?rp=` sans `initialTrip` — fiche Course d'un site custom, /book?mode=ride&rp=) : mêmes
  // adresses/verrous que la carte Course du tunnel classique, posés au chargement de la config.
  const [configTrip, setConfigTrip] = useState<RideInitialTrip | undefined>(undefined);
  const [pickupDatetime, setPickupDatetime] = useState<string>('');
  const [returnDatetime, setReturnDatetime] = useState<string>('');
  const [tripType, setTripType] = useState<TripType>('ONE_WAY');
  const [passengers, setPassengers] = useState<number>(1);
  const [smallLuggage, setSmallLuggage] = useState<number>(0);
  const [largeLuggage, setLargeLuggage] = useState<number>(0);
  const luggage = smallLuggage + largeLuggage;
  // Volet « Type de course » (cf. RideTypeSelection) : « Peu importe » par défaut en accès
  // direct ; { vehicleId } quand le client choisit un véhicule précis (config.vehicles présent) ;
  // { prestationId } en mode vitrine (« Réserver ce trajet ») OU via le sélecteur de formules du
  // mode fallback (config.vehicles absent — comportement historique).
  const [selection, setSelection] = useState<RideTypeSelection>('any');
  // Sélection restaurée d'un paiement en cours (retour Klarna / retour arrière) : les valeurs par
  // défaut posées ensuite (chargement de la config, formule vitrine) ne doivent pas l'écraser — un
  // re-checkout sans le véhicule choisi recotait un autre prix (RIDE_QUOTE_CHANGED) ou perdait le
  // véhicule. Un choix explicite du client passe toujours par setSelection.
  const selectionRestoredRef = useRef(false);
  const setDefaultSelection = (value: RideTypeSelection): void => {
    if (!selectionRestoredRef.current) setSelection(value);
  };
  // Pré-check consultatif de dispo (jamais bloquant) : avertit dès la saisie de l'heure
  // plutôt que d'échouer au paiement. null = rien à afficher.
  const [availabilityWarning, setAvailabilityWarning] = useState<string | null>(null);

  /* Refus inline d'une MODIFICATION inacceptable (capacité, dates, zone) : la saisie est
     rejetée (ou signalée immédiatement quand c'est un AUTRE champ qui devient incohérent)
     avec un message ancré au champ concerné — sans attendre « Voir le prix ». */
  // Capacité : ancré aux compteurs (tentative d'incrément au-delà de la sélection) ou au
  // volet « Type de course » (tentative de choisir un véhicule/formule trop petit).
  const [capacityError, setCapacityError] = useState<{ anchor: 'quantity' | 'selection'; message: string } | null>(
    null,
  );
  // Dates : message sous le champ refusé (prise en charge passée / retour avant l'aller).
  const [pickupDateError, setPickupDateError] = useState<string | null>(null);
  const [returnDateError, setReturnDateError] = useState<string | null>(null);
  // Zone de prise en charge invalidée par un changement d'HEURE (le pré-check porte le point) :
  // erreur externe du champ adresse — l'adresse reste en place, seul le message signale le conflit.
  const [pickupZoneError, setPickupZoneError] = useState<string | null>(null);
  // Dernière valeur ACCEPTÉE de chaque champ date : cible du revert au blur quand la saisie
  // clavier aboutit à une valeur refusée (le refus à la frappe casserait l'édition segment
  // par segment d'un datetime-local — jour édité avant le mois).
  const lastValidPickupRef = useRef<string>('');
  const lastValidReturnRef = useRef<string>('');

  const [quote, setQuote] = useState<RidePriceCalculatorResponseObject | null>(null);
  const [isComputing, setIsComputing] = useState<boolean>(false);
  /* Décomposition du prix dépliée à l'étape devis — repliée par défaut quand le devis porte
     PLUSIEURS véhicules (la liste des véhicules passe d'abord). En mono, elle reste affichée
     telle quelle : ce drapeau n'a alors aucun effet. */
  const [breakdownExpanded, setBreakdownExpanded] = useState<boolean>(false);

  /* Coordonnées */
  const [firstname, setFirstname] = useState<string>('');
  const [lastname, setLastname] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);

  /* Questionnaire du pro (champs personnalisés) — MÊME source (config publique ← même service
     backend) et MÊME règle que le tunnel classique : les dimensions prestation ET lieu se cumulent,
     une dimension vide ne contraint pas. Formule réservée = celle choisie (vitrine/fallback), sinon —
     « peu importe » ou véhicule précis — celle résolue par le devis (`quote.prestation_id`, connu avant
     l'étape coordonnées). Devis sans formule (backend antérieur) → aucune prestation réservée. Une
     course n'a PAS de lieu : un champ restreint à des lieux ne s'y applique jamais (ni affiché, ni
     requis, ni envoyé), même si la config en laissait passer un. */
  const customFields = useMemo((): BookingCustomField[] => readConfigCustomFields(config), [config]);
  /* Formules de la course réservée : celle CHOISIE (vitrine/fallback) prime ; sinon celles
     retenues par le devis — en multi-véhicules, l'UNION des formules des lignes
     (`prestation_ids`), puisque deux véhicules peuvent relever de formules différentes ; en mono,
     la formule unique `prestation_id` (comportement actuel, `prestation_ids` étant alors absent). */
  const bookedPrestationIds = useMemo((): number[] => {
    const selected = selectionPrestationIdOf(selection);
    if (selected != null) return [selected];
    const fromLines = quote?.prestation_ids;
    if (Array.isArray(fromLines) && fromLines.length > 0) return [...new Set(fromLines)];
    return quote?.prestation_id != null ? [quote.prestation_id] : [];
  }, [selection, quote?.prestation_ids, quote?.prestation_id]);
  const {
    visibleFields: visibleCustomFields,
    answers: customAnswers,
    errors: customErrors,
    setAnswers: setCustomAnswers,
    updateAnswer: updateCustomAnswer,
    blurField: blurCustomField,
    validate: validateCustomFields,
  } = useCustomFieldAnswers({
    fields: customFields,
    prestationIds: bookedPrestationIds,
    placeIds: NO_BOOKED_PLACE_IDS,
    requiredLabel: labels.required_field,
  });

  /* Paiement */
  const [checkoutResult, setCheckoutResult] = useState<RideCheckoutResponseObject | null>(null);
  const [paid, setPaid] = useState<boolean>(false);
  const [requestConfirmed, setRequestConfirmed] = useState<boolean>(false);
  const [payingOnSite, setPayingOnSite] = useState<boolean>(false);

  /* Paiement en cours de confirmation dans la section de paiement (Stripe puis vérification). */
  const [paymentBusy, setPaymentBusy] = useState<boolean>(false);

  /* Paiement repris après une redirection Stripe (voir useRedirectReturn ci-dessous). */
  const [returnedPayment, setReturnedPayment] = useState<{ paymentUuid: string; reservationUuid?: string } | null>(
    null,
  );

  /* Retour de redirection Stripe (Klarna…) : la page a été rechargée. On rouvre le paiement de la
     course et on restaure la saisie (trajet, devis, coordonnées) depuis le snapshot pris à l'ouverture
     du paiement ; sans snapshot (stockage indisponible), seul le pro_payment est connu. La course
     s'affiche dès que le backend voit le paiement réglé. */
  const redirectReturn = useRedirectReturn({
    param: REDIRECT_RETURN_PARAM.RIDE,
    enabled: handleRedirectReturn,
    poll: callbacks.onPollPaymentStatus,
    onSettled: ({ uuid, reservationUuid }): void => {
      setReturnedPayment({ paymentUuid: uuid, reservationUuid });
      setPaid(true);
    },
    /* La course a expiré (ou a été annulée) pendant le passage chez Klarna : plus rien à payer ni à
       annuler. On garde la saisie restaurée et on renvoie au trajet pour recalculer le prix. */
    onExpired: (uuid): void => {
      clearRidePaymentSnapshot(uuid);
      setReturnedPayment(null);
      setCheckoutResult(null);
      setQuote(null);
      setStep('trip');
    },
  });
  /* Remet la saisie d'un paiement en cours (retour Stripe ou retour arrière du navigateur). */
  const restorePaymentSnapshot = (snapshot: RidePaymentSnapshot): void => {
    setPickup(snapshot.pickup);
    setDropoff(snapshot.dropoff);
    setPickupDatetime(snapshot.pickupDatetime);
    setReturnDatetime(snapshot.returnDatetime);
    lastValidPickupRef.current = snapshot.pickupDatetime;
    lastValidReturnRef.current = snapshot.returnDatetime;
    setTripType(snapshot.tripType);
    setPassengers(snapshot.passengers);
    setSmallLuggage(snapshot.smallLuggage ?? 0);
    setLargeLuggage(snapshot.largeLuggage ?? snapshot.luggage);
    setQuote(snapshot.quote);
    if (snapshot.selection) {
      selectionRestoredRef.current = true;
      setSelection(snapshot.selection);
    }
    setFirstname(snapshot.firstname);
    setLastname(snapshot.lastname);
    setEmail(snapshot.email);
    setPhone(snapshot.phone);
    setNote(snapshot.note);
    if (snapshot.customAnswers) setCustomAnswers(snapshot.customAnswers);
    setCheckoutResult(snapshot.checkoutResult);
    setStep('details');
  };

  /* Identité de ce tunnel pour la reprise exclusive du paiement ouvert (claimOpenRidePayment). */
  const paymentOwnerRef = useRef<object>({});
  useEffect((): (() => void) => {
    const owner = paymentOwnerRef.current;
    return (): void => releaseOpenRidePayment(owner);
  }, []);

  const returnedPaymentUuid = redirectReturn.uuid;
  useEffect((): void => {
    if (!returnedPaymentUuid) return;
    // Ce tunnel a réclamé le retour Stripe : aucun autre tunnel de la page ne reprend ce paiement.
    claimOpenRidePayment(paymentOwnerRef.current);
    setReturnedPayment((current): { paymentUuid: string; reservationUuid?: string } =>
      current?.paymentUuid === returnedPaymentUuid ? current : { paymentUuid: returnedPaymentUuid },
    );
    const snapshot = loadRidePaymentSnapshot(returnedPaymentUuid);
    if (snapshot) restorePaymentSnapshot(snapshot);
    else setStep('details');
  }, [returnedPaymentUuid]);

  /* Retour arrière du navigateur depuis Klarna : la page revient SANS paramètre Stripe et le tunnel
     repartirait de zéro — un nouveau trajet sur le même créneau se heurterait à la course en attente
     du client, qui bloque son véhicule et son chauffeur. On reprend donc le paiement ouvert de
     l'onglet selon son statut backend. Décidé avant que le hook de retour ne nettoie l'URL. */
  const [resumingOpenPayment, setResumingOpenPayment] = useState<boolean>(
    (): boolean =>
      typeof window !== 'undefined' &&
      !hasRedirectReturn(REDIRECT_RETURN_PARAM.RIDE) &&
      loadOpenRidePayment() !== undefined,
  );
  useEffect((): (() => void) | undefined => {
    if (!resumingOpenPayment || !config) return undefined;
    const open = loadOpenRidePayment();
    if (
      !open ||
      open.snapshot.professionalId !== config.professional_id ||
      !claimOpenRidePayment(paymentOwnerRef.current)
    ) {
      setResumingOpenPayment(false);
      return undefined;
    }
    let cancelled = false;
    callbacks
      .onPollPaymentStatus(open.paymentUuid)
      .then((res): void => {
        if (cancelled) return;
        if (res.status === ProPaymentStatus.WAITING_PAYMENT) {
          restorePaymentSnapshot(open.snapshot);
        } else if (res.status === ProPaymentStatus.PAYED || res.status === ProPaymentStatus.AUTHORIZED) {
          // Klarna a abouti avant le retour arrière : la course est réglée, on l'affiche.
          restorePaymentSnapshot(open.snapshot);
          setPaid(true);
        } else {
          // Expiré / annulé : plus rien à reprendre, le créneau est libéré.
          clearRidePaymentSnapshot(open.paymentUuid);
        }
      })
      .catch((): void => {
        // Statut inconnu (réseau) : tunnel neuf, le snapshot reste pour un prochain chargement.
      })
      .finally((): void => {
        if (!cancelled) setResumingOpenPayment(false);
      });
    return (): void => {
      cancelled = true;
    };
  }, [resumingOpenPayment, config, callbacks]);
  const redirectNotice = redirectReturn.phase ? (
    <RedirectPaymentNotice
      phase={redirectReturn.phase}
      message={redirectReturnMessage(redirectReturn.phase, {
        verifying: labels.verifying,
        unverified: labels.payment_redirect_unverified,
        failed: labels.payment_redirect_failed,
        pending: labels.payment_redirect_pending,
        expired: labels.payment_redirect_expired,
      })}
    />
  ) : null;

  // Formule voulue par un éventuel deep-link (`?rp=`) ou trajet par défaut (`initialTrip`), figée
  // au montage : l'effet de chargement a `[callbacks]` en deps et ne doit pas se relancer si la
  // prop change après coup (un changement de `initialTrip` ou d'`initialPrestationId` est géré
  // par son propre effet ci-dessous).
  const wantedPrestationIdRef = useRef<number | undefined>(initialTrip?.prestation_id ?? initialPrestationId);

  // Applique la formule vitrine `id` depuis `cfg` : sélection verrouillée (chip en mode véhicules,
  // présélection en fallback) + trajet par défaut de la formule — adresses préremplies sans jamais
  // écraser une valeur déjà posée (initialTrip de la carte Course, ou saisie du client), verrous
  // via `configTrip` (cf. lockTrip ci-dessous). Renvoie false si la formule n'existe pas chez ce pro.
  const applyVitrineFormula = (cfg: RidePublicConfigResponseObject, id: number | undefined): boolean => {
    const target = id != null ? cfg.prestations.find((p): boolean => p.prestation_id === id) : undefined;
    if (!target) return false;
    setConfigTrip({
      prestation_id: target.prestation_id,
      pickup_address: target.ride_default_pickup_address,
      dropoff_address: target.ride_default_dropoff_address,
      pickup_locked: target.ride_pickup_address_locked,
      dropoff_locked: target.ride_dropoff_address_locked,
    });
    setPickup((cur): AddressObject | undefined => cur ?? target.ride_default_pickup_address);
    setDropoff((cur): AddressObject | undefined => cur ?? target.ride_default_dropoff_address);
    setDefaultSelection({ prestationId: target.prestation_id });
    return true;
  };

  // Chargé une fois au montage (les callbacks sont memoïsés par l'hôte).
  useEffect((): (() => void) => {
    let cancelled = false;
    callbacks
      .onLoadConfig()
      .then((cfg): void => {
        if (cancelled) return;
        setConfig(cfg);
        // Arrivée vitrine (« Réserver ce trajet » / deep-link `?rp=`) : formule verrouillée +
        // trajet par défaut. Sinon, accès direct :
        if (!applyVitrineFormula(cfg, wantedPrestationIdRef.current)) {
          if (readConfigVehicles(cfg).length > 0) {
            // Mode véhicules (nouveau contrat) : « Peu importe » par défaut.
            setDefaultSelection('any');
          } else {
            // Fallback (backend sans `vehicles`) — comportement historique : première formule
            // présélectionnée. « Peu importe » reste un choix explicite du client (pill dédiée).
            // Sans aucune formule, le tunnel rend l'état indisponible.
            const preselected = cfg.prestations[0]?.prestation_id;
            if (preselected != null) setDefaultSelection({ prestationId: preselected });
          }
        }
      })
      .catch((): void => {
        if (!cancelled) setLoadError(true);
      })
      .finally((): void => {
        if (!cancelled) setLoading(false);
      });
    return (): void => {
      cancelled = true;
    };
  }, [callbacks]);

  // Deep-link `?rp=` appliqué APRÈS le montage : quand l'onglet Trajet est le défaut de l'hôte
  // (sites custom : l'initialiseur SSR du widget pose « ride » dès le premier rendu), le tunnel
  // est déjà monté au moment où l'effet deep-link du parent résout `?rp=` — la prop
  // `initialPrestationId` arrive donc en RETARD sur wantedPrestationIdRef (figé au montage).
  // Même contrat que `initialTrip` : on ne réagit qu'au CHANGEMENT de la prop, jamais aux
  // choix ultérieurs du client. Config pas encore chargée → seul le ref est mis à jour, l'effet
  // de chargement ci-dessus appliquera la formule.
  const initialPrestationAppliedRef = useRef<number | undefined>(initialPrestationId);
  useEffect((): void => {
    if (initialPrestationId == null || initialPrestationAppliedRef.current === initialPrestationId) return;
    // Paiement repris après une redirection : le `?rp=` conservé dans l'URL de retour arrive en retard
    // (widget des sites) et ne doit pas effacer le devis restauré de la course en cours de paiement.
    if (returnedPayment) {
      initialPrestationAppliedRef.current = initialPrestationId;
      return;
    }
    initialPrestationAppliedRef.current = initialPrestationId;
    wantedPrestationIdRef.current = initialPrestationId;
    if (config && applyVitrineFormula(config, initialPrestationId)) {
      // Sélection changée après coup : un devis déjà calculé ne correspond plus (mêmes règles
      // que la saisie manuelle via onTripField).
      invalidateQuote();
    }
    // `config` en deps : si la prop arrive avant la config, le ref suffit (voir ci-dessus) ;
    // l'arrivée de la config ne ré-applique pas (ref déjà marqué appliqué), c'est l'effet de
    // chargement qui s'en charge via wantedPrestationIdRef.
  }, [initialPrestationId, config]);

  const ridePrestations = useMemo((): RidePublicPrestationObject[] => config?.prestations ?? [], [config]);
  const vehicles = useMemo((): RidePublicVehicleObject[] => readConfigVehicles(config), [config]);
  // Mode véhicules = le nouveau contrat backend expose des véhicules réservables ; sinon fallback
  // COMPLET sur le sélecteur de formules historique (rien ne change pour les anciens backends).
  const hasVehicles = vehicles.length > 0;
  // Projections de la sélection : au plus UNE des deux est définie (« peu importe » = aucune).
  const selectionPrestationId = selectionPrestationIdOf(selection);
  const selectedVehicleId = typeof selection === 'object' && 'vehicleId' in selection ? selection.vehicleId : undefined;
  const selectedPrestation = ridePrestations.find((p): boolean => p.prestation_id === selectionPrestationId);
  const selectedVehicle = vehicles.find((v): boolean => v.pro_resource_id === selectedVehicleId);
  // Vitrine (« Réserver ce trajet ») en mode véhicules : le sélecteur disparaît au profit d'un
  // chip de rappel non modifiable de la formule — la seule voie vers { prestationId } dans ce
  // mode est l'arrivée vitrine (initialTrip.prestation_id / deep-link `?rp=`).
  const vitrineMode = hasVehicles && selectedPrestation != null;

  /* ── Multi-véhicules ──────────────────────────────────────────────────
     TOUT ce qui suit est neutralisé tant que `multiVehicleActive` est faux : la composition
     n'est alors ni saisissable (pas de sélecteur de quantité) ni envoyée (buildSelectionPayload),
     et les compteurs gardent leurs plafonds mono. Le tunnel se comporte exactement comme avant. */
  const multiVehicleActive = isRideMultiVehicleActive(config);
  const maxVehicles = Math.min(config?.max_vehicles_per_booking ?? 1, RIDE_MAX_VEHICLES_PER_BOOKING);
  // Composition explicite courante (portée par `selection`, donc restaurée telle quelle par le
  // snapshot de paiement après une redirection Stripe).
  const composition = useMemo((): RideCompositionEntry[] => selectionCompositionOf(selection), [selection]);
  const compositionVehicleCount = countCompositionVehicles(composition);
  // Composition résolue sur le catalogue : bornes pax/bagages de chaque classe, pour le helper
  // partagé avec le planificateur backend. Classe inconnue (retirée du catalogue depuis le
  // snapshot) → bornes vides = non contraint : c'est le backend qui refusera, pas le tunnel.
  const compositionBoundsItems = (entries: readonly RideCompositionEntry[]): RideCompositionItem[] =>
    entries.map(
      (entry): RideCompositionItem => ({
        vehicle: vehicles.find((v): boolean => v.pro_resource_id === entry.vehicleId) ?? {},
        quantity: entry.quantity,
      }),
    );
  const compositionItems = useMemo(
    (): RideCompositionItem[] => compositionBoundsItems(composition),
    [composition, vehicles],
  );
  // Aiguillage mono / multi (cf. resolveMultiVehicleSelectionFields) : le groupe tient-il dans UN
  // véhicule ? Même règle que la sélection mono du backend, sur les véhicules de la formule imposée
  // (repli : ses bornes agrégées) ou, à défaut, sur tous les véhicules réservables.
  const groupFitsSingleVehicle = useMemo(
    (): boolean =>
      rideGroupFitsSingleVehicle(singleVehicleCandidates(selectedPrestation, vehicles), {
        passenger_count: passengers,
        luggage_count: luggage,
        small_luggage_count: smallLuggage,
        large_luggage_count: largeLuggage,
      }),
    [selectedPrestation, vehicles, passengers, luggage, smallLuggage, largeLuggage],
  );
  // Plafonds des compteurs : bornés par la capacité théorique du pro une fois le multi actif,
  // mono : 16 passagers et 20 bagages par taille tant qu'il est inactif.
  const passengerCounterMax = multiVehicleActive
    ? Math.min(RIDE_TUNNEL_MAX_PASSENGERS, config?.max_group_passengers ?? RIDE_TUNNEL_DEFAULT_MAX_PASSENGERS)
    : RIDE_TUNNEL_DEFAULT_MAX_PASSENGERS;
  const smallLuggageCounterMax = multiVehicleActive
    ? Math.min(RIDE_TUNNEL_MAX_LUGGAGE, config?.max_group_small_luggage ?? RIDE_TUNNEL_DEFAULT_MAX_LUGGAGE)
    : RIDE_TUNNEL_DEFAULT_MAX_LUGGAGE;
  const largeLuggageCounterMax = multiVehicleActive
    ? Math.min(RIDE_TUNNEL_MAX_LUGGAGE, config?.max_group_large_luggage ?? RIDE_TUNNEL_DEFAULT_MAX_LUGGAGE)
    : RIDE_TUNNEL_DEFAULT_MAX_LUGGAGE;
  /* Lignes véhicule du devis. VIDE dès que le devis ne porte qu'un véhicule (donc toujours en
     mono) : l'étape devis et le récapitulatif gardent alors leur rendu actuel, au pixel près. */
  const vehicleLines = quoteVehicleLines(quote);

  // Politique de paiement PAR formule (portée par la prestation « Course ») : la formule choisie
  // avant devis, puis la formule EFFECTIVEMENT retenue par le devis (autoritaire en « peu importe »,
  // où aucune formule n'est présélectionnée → on ne la connaît qu'après le calcul du prix).
  const paymentType = quote?.payment_type ?? selectedPrestation?.payment_type ?? PrestationPaymentType.FREE;
  const isFree = paymentType === PrestationPaymentType.FREE;
  // Politique « paiement intégral optionnel » : le client peut payer en ligne OU choisir de régler
  // sur place (confirme la course sans paiement, via l'endpoint public confirm-pay-on-site).
  const isPayOnSite = paymentType === PrestationPaymentType.FULL_PAYMENT_OPTIONAL;
  // Le pro valide manuellement chaque réservation : une course PAYÉE reste en attente de sa
  // confirmation → l'écran de fin annonce « paiement reçu, en attente » plutôt que « confirmée ».
  const requiresProConfirmation = config?.requires_pro_confirmation ?? false;
  const paymentUuid = checkoutResult
    ? checkoutResult.requires_payment
      ? (checkoutResult.payment_uuid ?? null)
      : null
    : (returnedPayment?.paymentUuid ?? null);
  const reservationUuid = checkoutResult?.reservation_uuid ?? returnedPayment?.reservationUuid;
  /* Issue du paiement inconnue : Stripe confirme (ou a confirmé) mais le backend ne l'a pas encore vu.
     Tant que c'est le cas, le checkout ne peut être ni abandonné ni refait. */
  const paymentLocked = paymentBusy || redirectReturn.isChecking;

  /* Snapshot de la saisie à l'ouverture du paiement : restauré si un moyen à redirection recharge la
     page (voir le retour ci-dessus). Le formulaire est figé tant que le checkout existe (toute saisie
     l'invalide), les valeurs lues ici sont donc celles du paiement. */
  useEffect((): void => {
    if (!config || !checkoutResult?.requires_payment || !checkoutResult.payment_uuid) return;
    saveRidePaymentSnapshot(checkoutResult.payment_uuid, {
      professionalId: config.professional_id,
      checkoutResult,
      quote,
      selection,
      pickup,
      dropoff,
      pickupDatetime,
      returnDatetime,
      tripType,
      passengers,
      luggage,
      smallLuggage,
      largeLuggage,
      firstname,
      lastname,
      email,
      phone,
      note,
      customAnswers,
    });
  }, [
    config,
    checkoutResult,
    quote,
    selection,
    pickup,
    dropoff,
    pickupDatetime,
    returnDatetime,
    tripType,
    passengers,
    luggage,
    smallLuggage,
    largeLuggage,
    firstname,
    lastname,
    email,
    phone,
    note,
    customAnswers,
  ]);
  useEffect((): void => {
    if ((paid || requestConfirmed) && paymentUuid) clearRidePaymentSnapshot(paymentUuid);
  }, [paid, requestConfirmed, paymentUuid]);

  /* Le devis et le checkout sont figés sur les valeurs du formulaire au moment du calcul :
     toute modification du trajet les invalide (le backend recalcule toujours depuis les
     valeurs COURANTES — on ne débite jamais un prix qui n'a pas été affiché). */
  // Jeton d'obsolescence : bumpé à chaque invalidation (donc à chaque changement de champ via
  // onTripField). Un calcul de prix en vol capture le jeton au départ et jette sa réponse si le
  // jeton a changé entre-temps — sinon un devis calculé sur d'ANCIENNES valeurs s'affichait avec
  // les NOUVELLES saisies (prix/tracé faux).
  const computeTokenRef = useRef(0);
  /* Annulation en vol de la course abandonnée : le prochain checkout l'attend, sinon la course en
     attente occupe encore le véhicule et le chauffeur au moment de la nouvelle attribution. */
  const pendingReleaseRef = useRef<Promise<void> | null>(null);
  /* Le client abandonne le checkout en cours (modifie son trajet ou ses coordonnées) : sa course en
     attente de paiement bloquerait son véhicule et son chauffeur jusqu'à expiration (10 min) et ferait
     échouer — ou doublerait — la nouvelle réservation. Le backend n'annule qu'un paiement encore en
     attente : un paiement déjà abouti n'est jamais touché. */
  const releasePendingRide = (): void => {
    const pendingUuid = checkoutResult
      ? checkoutResult.requires_payment
        ? checkoutResult.payment_uuid
        : undefined
      : returnedPayment?.paymentUuid;
    // Paiement peut-être déjà encaissé (confirmation ou vérification en cours) : ne jamais l'annuler.
    if (!pendingUuid || paid || paymentLocked) return;
    clearRidePaymentSnapshot(pendingUuid);
    pendingReleaseRef.current = callbacks.onCancelPendingRide(pendingUuid).catch((): void => {
      // Échec : l'expiration libérera la course ; le checkout suivant remontera l'indisponibilité.
    });
  };
  const invalidateQuote = (): void => {
    computeTokenRef.current += 1;
    releasePendingRide();
    setQuote(null);
    setCheckoutResult(null);
  };
  const invalidateCheckout = (): void => {
    releasePendingRide();
    setCheckoutResult(null);
    // Paiement repris après une redirection : le trajet saisi avant n'existe plus, on repart du début.
    if (returnedPayment) {
      setReturnedPayment(null);
      // Sans snapshot restauré, il n'y a plus de devis sur lequel refaire le checkout.
      if (!quote) setStep('trip');
    }
  };
  const onTripField = <T,>(setter: (value: T) => void): ((value: T) => void) => {
    return (value: T): void => {
      setter(value);
      invalidateQuote();
    };
  };

  /* ── Refus inline des modifications inacceptables ──────────────────── */

  // Bornes de la sélection courante : composition explicite > véhicule précis > bornes du GROUPE
  // (multi actif) > formule > « peu importe » (agrégat du catalogue — refuser au-delà du max des
  // maxes est sûr, aucun candidat ne peut accueillir).
  const capacityBounds = useMemo((): { paxMax: number; smallLuggageMax: number; largeLuggageMax: number } => {
    // Composition explicite : bornes CUMULÉES (Σ pax_max × quantité), calculées par le helper
    // partagé avec le planificateur backend.
    if (compositionItems.length > 0) {
      const bounds = sumRideCompositionBounds(compositionItems);
      return {
        paxMax: finiteCapacityBound(bounds.passenger_max),
        smallLuggageMax: finiteCapacityBound(bounds.small_luggage_max),
        largeLuggageMax: finiteCapacityBound(bounds.large_luggage_max),
      };
    }
    if (selectedVehicle) return aggregateCapacityBounds([selectedVehicle]);
    // Multi actif hors composition (« Automatique » ou vitrine) : le serveur RÉPARTIT le groupe —
    // la contrainte n'est plus la plus grosse classe, mais la capacité théorique du pro.
    if (multiVehicleActive)
      return {
        paxMax: passengerCounterMax,
        smallLuggageMax: smallLuggageCounterMax,
        largeLuggageMax: largeLuggageCounterMax,
      };
    if (selectedPrestation) return aggregateCapacityBounds([selectedPrestation]);
    return aggregateCapacityBounds(hasVehicles ? vehicles : ridePrestations);
  }, [
    compositionItems,
    selectedVehicle,
    multiVehicleActive,
    passengerCounterMax,
    smallLuggageCounterMax,
    largeLuggageCounterMax,
    selectedPrestation,
    hasVehicles,
    vehicles,
    ridePrestations,
  ]);

  // Compteurs : un incrément AU-DELÀ de la borne est refusé (message ; la valeur ne bouge pas).
  // Décrémenter reste toujours possible — c'est la voie de sortie d'un état hors borne.
  const handlePassengersChange = (next: number): void => {
    if (next > passengers && next > capacityBounds.paxMax) {
      setCapacityError({
        anchor: 'quantity',
        message: labels.error_max_passengers.replace('{max}', String(capacityBounds.paxMax)),
      });
      return;
    }
    setCapacityError(null);
    onTripField(setPassengers)(next);
  };
  const handleLuggageChange = (kind: 'small' | 'large', next: number): void => {
    const current = kind === 'small' ? smallLuggage : largeLuggage;
    const max = kind === 'small' ? capacityBounds.smallLuggageMax : capacityBounds.largeLuggageMax;
    if (next > current && next > max) {
      setCapacityError({
        anchor: 'quantity',
        message: (kind === 'small' ? labels.error_max_small_luggage : labels.error_max_large_luggage).replace(
          '{max}',
          String(max),
        ),
      });
      return;
    }
    setCapacityError(null);
    onTripField(kind === 'small' ? setSmallLuggage : setLargeLuggage)(next);
  };

  // Volet « Type de course » : choisir un véhicule/formule trop petit pour la saisie courante
  // est refusé (la sélection précédente reste active). « Peu importe » est toujours acceptable.
  const trySelect = (next: RideTypeSelection): void => {
    const target =
      // Une composition multi ne passe jamais par ici (cf. handleVehicleQuantityChange, qui
      // applique la règle du planificateur) : rien à contrôler sur une classe isolée.
      next === 'any' || 'vehicles' in next
        ? undefined
        : 'vehicleId' in next
          ? vehicles.find((v): boolean => v.pro_resource_id === next.vehicleId)
          : ridePrestations.find((p): boolean => p.prestation_id === next.prestationId);
    if (
      target &&
      ((target.pax_max != null && passengers > target.pax_max) ||
        (target.small_luggage_max != null && smallLuggage > target.small_luggage_max) ||
        (target.large_luggage_max != null && largeLuggage > target.large_luggage_max))
    ) {
      setCapacityError({ anchor: 'selection', message: labels.error_selection_capacity });
      return;
    }
    setCapacityError(null);
    onTripField(setSelection)(next);
  };

  /**
   * Sélecteur de quantité d'une classe (mode multi) : 0 = classe non retenue, tout à 0 = retour à
   * « Automatique ». Seuls les AJOUTS sont contrôlés — retirer un véhicule reste toujours possible,
   * c'est la voie de sortie d'un état refusé.
   *
   * Deux refus immédiats, avec la règle du planificateur backend (helper partagé) :
   * plafond de véhicules par réservation, et composition dont la somme des MINIMUMS dépasse déjà le
   * groupe (plus de véhicules que de passagers, ou minimum d'une classe non atteint). Une
   * composition trop PETITE, elle, reste un état intermédiaire légitime pendant qu'on la
   * construit : elle est refusée à « Voir le prix » (validateTrip), pas à chaque clic.
   */
  const handleVehicleQuantityChange = (vehicleId: number, next: number): void => {
    const draft: RideCompositionEntry[] = vehicles
      .map((vehicle): RideCompositionEntry | undefined => {
        const quantity =
          vehicle.pro_resource_id === vehicleId
            ? Math.max(0, next)
            : (composition.find((item): boolean => item.vehicleId === vehicle.pro_resource_id)?.quantity ?? 0);
        return quantity > 0 ? { vehicleId: vehicle.pro_resource_id, quantity } : undefined;
      })
      .filter((item): item is RideCompositionEntry => item !== undefined);
    const nextCount = countCompositionVehicles(draft);
    if (nextCount > compositionVehicleCount) {
      if (nextCount > maxVehicles) {
        setCapacityError({
          anchor: 'selection',
          message: labels.error_max_vehicles.replace('{max}', String(maxVehicles)),
        });
        return;
      }
      const bounds = sumRideCompositionBounds(compositionBoundsItems(draft));
      if (
        bounds.passenger_min > passengers ||
        bounds.small_luggage_min > smallLuggage ||
        bounds.large_luggage_min > largeLuggage
      ) {
        setCapacityError({ anchor: 'selection', message: compositionOverflowMessage(labels, nextCount, passengers) });
        return;
      }
    }
    setCapacityError(null);
    onTripField(setSelection)(toRideSelection(draft));
  };

  // Dates : la valeur saisie est signalée immédiatement (message sous le champ) puis REFUSÉE au
  // blur — revert vers la dernière valeur acceptée. Valider au blur (et non à la frappe) laisse
  // l'édition clavier segment par segment d'un datetime-local aboutir avant le verdict.
  const isPickupAcceptable = (value: string): boolean =>
    !value || value > nowWallTimeInTimezone(config?.timezone ?? 'Europe/Paris');
  const handlePickupDatetimeChange = (value: string): void => {
    onTripField(setPickupDatetime)(value);
    const acceptable = isPickupAcceptable(value);
    setPickupDateError(acceptable ? null : labels.error_datetime_past);
    if (acceptable) {
      lastValidPickupRef.current = value;
      // L'aller déplacé APRÈS un retour déjà saisi : le conflit est signalé sous le champ
      // retour (c'est LUI qui devient incohérent — l'aller reste une valeur acceptable).
      setReturnDateError(isReturnAfterPickup(returnDatetime, value) ? null : labels.error_return_before_pickup);
    }
  };
  const handlePickupDatetimeBlur = (): void => {
    if (isPickupAcceptable(pickupDatetime)) return;
    // Refus : on restaure la dernière valeur acceptée — le message reste affiché, il explique
    // pourquoi la modification n'a pas été conservée.
    onTripField(setPickupDatetime)(lastValidPickupRef.current);
  };
  const handleReturnDatetimeChange = (value: string): void => {
    onTripField(setReturnDatetime)(value);
    const acceptable = isReturnAfterPickup(value, pickupDatetime);
    setReturnDateError(acceptable ? null : labels.error_return_before_pickup);
    if (acceptable) lastValidReturnRef.current = value;
  };
  const handleReturnDatetimeBlur = (): void => {
    if (isReturnAfterPickup(returnDatetime, pickupDatetime)) return;
    onTripField(setReturnDatetime)(lastValidReturnRef.current);
  };
  const handleTripTypeChange = (next: TripType): void => {
    // Le champ retour disparaît en aller simple : son erreur ne doit pas survivre au switch.
    if (next === 'ONE_WAY') setReturnDateError(null);
    onTripField(setTripType)(next);
  };

  // Garde de sélection de l'adresse de DÉPART (seule adresse contrainte par une zone côté
  // backend) : quand l'heure de prise en charge est déjà connue, on interroge le pré-check avec
  // le point candidat — hors zone ⇒ la sélection est REFUSÉE avant d'être commise. Sans heure,
  // la zone dépend des fenêtres de dispo couvrantes : rien à vérifier ici, le pré-check prend
  // le relais dès l'heure saisie (pickupZoneError). Consultatif : toute erreur API vaut
  // acceptation, le devis/checkout restent l'autorité.
  /**
   * Sélection du volet « Type de course », envoyée À L'IDENTIQUE sur les TROIS appels (devis,
   * pré-check de dispo, checkout) : toute divergence change l'empreinte du devis recalculée au
   * checkout, qui refuse alors en 409 RIDE_QUOTE_CHANGED. Source unique, d'où l'extraction.
   *
   * - vitrine / fallback → `prestation_id` (jamais `vehicle_id`) ;
   * - véhicule précis (mono) → `vehicle_id` ;
   * - « peu importe » (mono) → aucun des deux : le backend affecte le véhicule le moins cher.
   *
   * Les champs multi ne sont ajoutés QUE si le multi est actif (réglage du pro) — coupé, le payload
   * est strictement le mono. Actif, ils ne quittent le mono que pour une demande que le mono ne
   * sait pas servir (cf. resolveMultiVehicleSelectionFields).
   */
  const buildSelectionPayload = (): {
    prestation_id?: number;
    vehicle_id?: number;
    vehicles?: RideVehicleCompositionItemObject[];
    allow_split?: boolean;
  } => ({
    ...(selectionPrestationId ? { prestation_id: selectionPrestationId } : {}),
    ...(selectedVehicleId ? { vehicle_id: selectedVehicleId } : {}),
    ...(multiVehicleActive
      ? resolveMultiVehicleSelectionFields({ composition, selectedVehicleId, groupFitsSingleVehicle })
      : {}),
  });

  const validatePickupSelection = async (address: AddressObject): Promise<string | null> => {
    if (!config || ridePrestations.length === 0) return null;
    if (!pickupDatetime || pickupDatetime <= nowWallTimeInTimezone(config.timezone ?? 'Europe/Paris')) return null;
    const res = await callbacks.onCheckAvailability({
      ...buildSelectionPayload(),
      pickup_datetime: pickupDatetime,
      trip_type: tripType,
      ...(tripType === 'ROUND_TRIP' && returnDatetime ? { return_datetime: returnDatetime } : {}),
      passenger_count: passengers,
      luggage_count: luggage,
      small_luggage_count: smallLuggage,
      large_luggage_count: largeLuggage,
      pickup_latitude: address.latitude,
      pickup_longitude: address.longitude,
    });
    if (!res.available && res.reason === 'PICKUP_OUTSIDE_ZONE') return labels.error_pickup_outside_zone;
    return null;
  };

  // Adresse verrouillée par le pro (navette) : non modifiable par le client — champ désactivé +
  // mention « Adresse fixe ». Le verrou ne vaut que si l'adresse correspondante est bien fournie.
  // Source : le trajet posé par l'hôte (carte Course du tunnel classique) prime ; sinon le trajet
  // par défaut de la formule vitrine résolu depuis la config (arrivée deep-link `?rp=`).
  const lockTrip = initialTrip ?? configTrip;
  const pickupLocked = lockTrip?.pickup_locked === true && lockTrip.pickup_address != null;
  const dropoffLocked = lockTrip?.dropoff_locked === true && lockTrip.dropoff_address != null;

  // Préremplissage du trajet par défaut (carte « Course » du tunnel classique) : appliqué à
  // CHAQUE changement de référence de la prop (le montage est couvert par les initialiseurs
  // useState + wantedPrestationIdRef). Une adresse absente n'écrase pas la saisie du client ;
  // la formule n'est appliquée que si elle existe chez ce pro (au montage, config est encore
  // null → la présélection est faite par l'effet de chargement via wantedPrestationIdRef).
  // Tout préremplissage invalide le devis (mêmes règles que la saisie manuelle) et ramène à
  // l'étape trajet pour que le client voie ce qui a été prérempli.
  const initialTripAppliedRef = useRef<RideInitialTrip | undefined>(initialTrip);
  useEffect((): void => {
    if (!initialTrip || initialTripAppliedRef.current === initialTrip) return;
    initialTripAppliedRef.current = initialTrip;
    wantedPrestationIdRef.current = initialTrip.prestation_id;
    if (initialTrip.pickup_address) setPickup(initialTrip.pickup_address);
    if (initialTrip.dropoff_address) setDropoff(initialTrip.dropoff_address);
    if (
      initialTrip.prestation_id != null &&
      config?.prestations.some((p): boolean => p.prestation_id === initialTrip.prestation_id)
    ) {
      // En mode véhicules, cette forme { prestationId } re-verrouille le volet en chip vitrine ;
      // en fallback elle présélectionne la formule dans le sélecteur historique.
      setSelection({ prestationId: initialTrip.prestation_id });
    }
    invalidateQuote();
    setStep('trip');
    // `config` volontairement hors deps : l'effet ne réagit qu'à la référence de `initialTrip`
    // (la présélection au chargement de config passe par wantedPrestationIdRef).
  }, [initialTrip]);

  const resolveError = (e: unknown): string => {
    // Cut builder : `message` est un texte utilisateur déjà traduit — affiché tel quel
    // (le résoudre comme clé API le remplaçait par error_generic).
    if (isBuilderCutError(e)) return e.message;
    // PublicApiError porte la clé API dans `errorKey` (mappée par resolveApiErrorLabel →
    // API_ERRORS) ; `message` est déjà un texte générique. Lire `errorKey` en priorité,
    // sinon on retombait toujours sur error_generic.
    const key = (e as { errorKey?: string })?.errorKey ?? (e instanceof Error ? e.message : '');
    return (key && resolveApiErrorLabel?.(key)) || labels.error_generic;
  };

  // Pré-check de disponibilité débouncé (~500 ms) dès que les heures saisies sont localement
  // valides : avertit qu'aucun véhicule n'est libre AVANT le paiement. CONSULTATIF : inline,
  // non bloquant (le CTA reste actif), silencieux sur erreur API — le checkout revalide sous
  // verrou et reste l'autorité.
  useEffect((): (() => void) | undefined => {
    setAvailabilityWarning(null);
    setPickupZoneError(null);
    if (!config) return undefined;
    if (!pickupDatetime || pickupDatetime <= nowWallTimeInTimezone(config.timezone ?? 'Europe/Paris')) {
      return undefined;
    }
    if (tripType === 'ROUND_TRIP' && returnDatetime && new Date(returnDatetime) <= new Date(pickupDatetime)) {
      return undefined;
    }
    // « Peu importe » (selection === 'any') est valide dès qu'il existe des formules — le
    // pré-check évalue alors tout le pool. On ne skippe que si le pro n'a aucune formule.
    if (ridePrestations.length === 0) return undefined;
    // Paiement ouvert : le créneau est tenu par la course en attente du client lui-même — le
    // pré-check la compterait comme occupée (« aucun chauffeur / véhicule ») sans rien apprendre.
    if (paymentUuid) return undefined;

    // MÊME sélection que le devis et le checkout (cohérence du fingerprint) : formule, véhicule
    // précis, ou composition / répartition automatique en multi.
    const selectionPayload = buildSelectionPayload();
    let cancelled = false;
    const timer = setTimeout((): void => {
      // Course abandonnée en cours d'annulation : attendre sa libération, sinon le pré-check la voit
      // encore et affiche une indisponibilité qui ne se relancerait pas ensuite.
      void (pendingReleaseRef.current ?? Promise.resolve())
        .then(
          (): ReturnType<RideCallbacks['onCheckAvailability']> =>
            callbacks.onCheckAvailability({
              ...selectionPayload,
              pickup_datetime: pickupDatetime,
              trip_type: tripType,
              ...(tripType === 'ROUND_TRIP' && returnDatetime ? { return_datetime: returnDatetime } : {}),
              passenger_count: passengers,
              luggage_count: luggage,
              small_luggage_count: smallLuggage,
              large_luggage_count: largeLuggage,
              // Durée d'un devis déjà calculé pour dimensionner les fenêtres (sinon défaut serveur).
              ...(quote?.estimated_duration_min
                ? { estimated_duration_min: Math.max(1, Math.min(1440, Math.round(quote.estimated_duration_min))) }
                : {}),
              // Point de prise en charge déjà saisi : active le check de zone d'intervention côté
              // backend (cause PICKUP_OUTSIDE_ZONE, ancrée au champ adresse — cas « adresse choisie
              // AVANT l'heure », que la garde de sélection ne peut pas couvrir).
              ...(pickup ? { pickup_latitude: pickup.latitude, pickup_longitude: pickup.longitude } : {}),
            }),
        )
        .then((res): void => {
          if (cancelled) return;
          // Nombre MINIMAL de véhicules nécessaires au groupe (absent = 1, courses mono).
          const vehicleCount = res.vehicle_count ?? 1;
          if (res.available) {
            // Deux notes informatives, non bloquantes, cumulables :
            // - le groupe demande plusieurs véhicules (le client doit le savoir avant le prix) ;
            // - surbooking accepté par le pro : flotte saturée mais la demande sera transmise.
            const notices = [
              ...(vehicleCount >= 2 ? [labels.slot_multi_vehicle_notice.replace('{count}', String(vehicleCount))] : []),
              ...(res.overbooking ? [labels.slot_overbooked_notice] : []),
            ];
            setAvailabilityWarning(notices.length > 0 ? notices.join(' ') : null);
            return;
          }
          // Causes temporelles (fenêtres d'ouverture, délai min, horizon) : message dédié.
          // Sans reason (rétro-compat) ou NO_VEHICLE : logique véhicule historique par leg.
          const reason = res.reason;
          if (reason === 'PICKUP_OUTSIDE_ZONE') {
            // Hors zone : le message vit sous le champ adresse (pas dans la bannière de dispo) —
            // c'est l'ADRESSE qu'il faut corriger, pas l'horaire.
            setPickupZoneError(labels.error_pickup_outside_zone);
          } else if (reason === 'GROUP_TOO_LARGE' && selectionPayload.vehicles) {
            // Composition explicite (≥ 2 véhicules, envoyée en `vehicles`) : le refus porte sur LA
            // composition choisie, pas sur la flotte du pro (« Automatique » servirait peut-être le
            // groupe). Irréalisable au sens du planificateur → même message que « Voir le prix »
            // (validateTrip), selon le sens de l'écart, jamais « groupe trop grand ». Réalisable →
            // c'est la flotte qui n'a pas assez d'unités de ces classes : même message que la cause
            // NO_VEHICLE ci-dessous (ancien backend, qui ne la distinguait pas encore).
            setAvailabilityWarning(
              isRideCompositionFeasible(compositionItems, {
                passenger_count: passengers,
                luggage_count: luggage,
                small_luggage_count: smallLuggage,
                large_luggage_count: largeLuggage,
              })
                ? labels.error_composition_exceeds_fleet
                : compositionRefusalMessage(labels, compositionItems, {
                    passengers,
                    smallLuggage,
                    largeLuggage,
                  }),
            );
          } else if (reason === 'NO_VEHICLE' && selectionPayload.vehicles) {
            // Composition explicite sans assez d'unités de ces classes à cette heure : changer
            // d'horaire n'y fera souvent rien (flotte trop courte) — il faut réduire le nombre de
            // ces véhicules ou revenir à « Automatique ».
            setAvailabilityWarning(labels.error_composition_exceeds_fleet);
          } else if (reason === 'GROUP_TOO_LARGE') {
            // Aucune composition dans la limite ne peut accueillir le groupe : ce n'est pas
            // l'horaire qu'il faut changer, mais le nombre de passagers / bagages.
            setAvailabilityWarning(labels.slot_group_too_large);
          } else if (reason === 'TOO_SOON') {
            setAvailabilityWarning(labels.slot_too_soon);
          } else if (reason === 'TOO_FAR') {
            setAvailabilityWarning(labels.slot_too_far);
          } else if (reason === 'NO_DRIVER') {
            // Un chauffeur par véhicule : au-delà d'un véhicule, ce sont les chauffeurs qui manquent.
            setAvailabilityWarning(vehicleCount >= 2 ? labels.slot_not_enough_drivers : labels.slot_no_driver);
          } else if (reason === 'OUTSIDE_OPENING_HOURS') {
            setAvailabilityWarning(
              res.unavailable_leg === RideTripLeg.RETURN
                ? labels.slot_outside_opening_hours_return
                : labels.slot_outside_opening_hours,
            );
          } else if (res.unavailable_leg === RideTripLeg.RETURN) {
            setAvailabilityWarning(labels.slot_unavailable_return);
          } else if (res.unavailable_leg === RideTripLeg.OUTBOUND) {
            setAvailabilityWarning(labels.slot_unavailable_outbound);
          } else {
            setAvailabilityWarning(labels.slot_unavailable);
          }
        })
        .catch((): void => {
          // Check consultatif : une erreur API ne doit ni bloquer ni polluer le formulaire.
        });
    }, 500);
    return (): void => {
      cancelled = true;
      clearTimeout(timer);
    };
    // Dépendance sur la SEULE durée du devis (pas l'objet `quote` entier) : un recalcul de devis
    // qui ne change pas la durée ne doit pas re-déclencher le pré-check (POST /availability).
  }, [
    config,
    ridePrestations.length,
    pickup,
    pickupDatetime,
    returnDatetime,
    tripType,
    passengers,
    luggage,
    smallLuggage,
    largeLuggage,
    // `selection` couvre la formule, le véhicule précis ET la composition multi (identité stable
    // entre deux rendus : seul setSelection la change).
    selection,
    multiVehicleActive,
    quote?.estimated_duration_min,
    paymentUuid,
    callbacks,
    labels,
  ]);

  const validateTrip = (): string | null => {
    if (!pickup || !dropoff) return labels.error_addresses_required;
    // Comparaison dans le fuseau du PRO (cf. nowWallTimeInTimezone) : l'ancien `new Date(...) <=
    // Date.now()` interprétait l'heure murale dans le fuseau du navigateur → faux refus (client à
    // l'est du pro) ou faux passage (client à l'ouest) dès que les fuseaux diffèrent.
    if (!pickupDatetime || pickupDatetime <= nowWallTimeInTimezone(config.timezone ?? 'Europe/Paris'))
      return labels.error_datetime_past;
    // Un aller-retour exige une heure de retour : sinon le prix double (trip_type) mais rien ne
    // matérialise le retour (récap/email conditionnés à returnDatetime) → tarif « aller simple » ×2.
    if (tripType === 'ROUND_TRIP' && !returnDatetime) return labels.error_return_required;
    if (tripType === 'ROUND_TRIP' && returnDatetime && new Date(returnDatetime) <= new Date(pickupDatetime)) {
      return labels.error_return_before_pickup;
    }
    // Composition explicite : contrôle local avec la règle EXACTE du planificateur backend
    // (intervalles [Σmin, Σmax] sur les passagers ET les bagages) — helper partagé. Évite de
    // découvrir RIDE_GROUP_TOO_LARGE seulement après l'appel au devis. Message selon le sens de
    // l'écart : trop de véhicules pour le groupe, ou composition trop petite.
    if (composition.length > 0) {
      if (
        !isRideCompositionFeasible(compositionItems, {
          passenger_count: passengers,
          luggage_count: luggage,
          small_luggage_count: smallLuggage,
          large_luggage_count: largeLuggage,
        })
      ) {
        return compositionRefusalMessage(labels, compositionItems, { passengers, smallLuggage, largeLuggage });
      }
      return null;
    }
    // Formule SÉLECTIONNÉE trop petite → message immédiat (capacités déjà en config, inutile
    // d'attendre RIDE_VEHICLE_NOT_FOUND). Bornes = agrégats du pool (min des mins / max des maxes).
    // En multi, le serveur RÉPARTIT le groupe sur le pool : la capacité d'un seul véhicule ne
    // borne plus rien, on laisse le backend (et le pré-check) trancher.
    if (selectionPrestationId != null) {
      if (!selectedPrestation) return labels.error_generic;
      const paxMax = selectedPrestation.pax_max ?? Number.MAX_SAFE_INTEGER;
      const bounds = aggregateCapacityBounds([selectedPrestation]);
      if (
        !multiVehicleActive &&
        (passengers > paxMax || smallLuggage > bounds.smallLuggageMax || largeLuggage > bounds.largeLuggageMax)
      )
        return labels.error_vehicle_capacity;
    }
    // Même garde immédiate pour un VÉHICULE précis (bornes déjà en config) — borne absente =
    // pas de contrainte sur cet axe (le backend reste l'autorité).
    if (selectedVehicleId != null) {
      if (!selectedVehicle) return labels.error_generic;
      const paxMax = selectedVehicle.pax_max ?? Number.MAX_SAFE_INTEGER;
      const bounds = aggregateCapacityBounds([selectedVehicle]);
      if (passengers > paxMax || smallLuggage > bounds.smallLuggageMax || largeLuggage > bounds.largeLuggageMax)
        return labels.error_vehicle_capacity;
    }
    return null;
  };

  const buildTripPayload = (): {
    pickup_address: AddressObject;
    dropoff_address: AddressObject;
    pickup_datetime: string;
    trip_type: TripType;
    return_datetime?: string;
    passenger_count: number;
    luggage_count: number;
    small_luggage_count: number;
    large_luggage_count: number;
    prestation_id?: number;
    vehicle_id?: number;
    vehicles?: RideVehicleCompositionItemObject[];
    allow_split?: boolean;
  } => ({
    pickup_address: pickup as AddressObject,
    dropoff_address: dropoff as AddressObject,
    // Heures « murales » du lieu de prise en charge, envoyées SANS conversion : le backend les
    // interprète dans le fuseau du PRO. Un `toISOString()` ici les interpréterait dans le fuseau
    // du NAVIGATEUR — heure d'agenda et tarif jour/nuit faux dès que le client réserve depuis
    // un autre fuseau que celui du pro.
    pickup_datetime: pickupDatetime,
    trip_type: tripType,
    ...(tripType === 'ROUND_TRIP' && returnDatetime ? { return_datetime: returnDatetime } : {}),
    // `passenger_count` / `luggage_count` restent les totaux du GROUPE en multi : c'est le
    // backend qui répartit entre les véhicules.
    passenger_count: passengers,
    luggage_count: luggage,
    small_luggage_count: smallLuggage,
    large_luggage_count: largeLuggage,
    // Sélection du volet « Type de course », identique sur les TROIS appels (cf. buildSelectionPayload).
    ...buildSelectionPayload(),
  });

  const handleComputePrice = async (): Promise<void> => {
    const validationError = validateTrip();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setIsComputing(true);
    // Jeton capturé AVANT l'appel : si un champ change pendant l'await (→ invalidateQuote le bumpe),
    // la réponse est obsolète et on la jette plutôt que d'afficher un prix calculé sur d'anciennes valeurs.
    const token = computeTokenRef.current;
    try {
      const freshQuote = await callbacks.onComputePrice(buildTripPayload());
      if (token !== computeTokenRef.current) return;
      setQuote(freshQuote);
      // Aller-retour : le retour doit commencer après la fin ESTIMÉE de l'aller (départ + durée),
      // sinon les deux trajets se chevauchent sur le même véhicule (le backend le refuse aussi).
      // Vérifié ICI (durée connue) pour corriger l'heure sans quitter le formulaire.
      if (
        tripType === 'ROUND_TRIP' &&
        returnDatetime &&
        new Date(returnDatetime).getTime() <
          new Date(pickupDatetime).getTime() + freshQuote.estimated_duration_min * 60_000
      ) {
        setError(labels.error_return_before_outbound_end);
        return;
      }
      setStep('quote');
    } catch (e) {
      setQuote(null);
      setError(resolveError(e));
    } finally {
      setIsComputing(false);
    }
  };

  const goToDetails = (): void => {
    if (!quote) return;
    setError(null);
    setStep('details');
  };

  const back = (): void => {
    setError(null);
    setStep(step === 'details' ? 'quote' : 'trip');
  };

  const handleCheckout = async (): Promise<void> => {
    setError(null);
    // Coordonnées ET questionnaire contrôlés ensemble : les champs requis non renseignés sont
    // signalés sous chaque champ (comme le tunnel classique), même si les coordonnées sont aussi
    // incomplètes.
    const contactValid = Boolean(firstname.trim() && lastname.trim() && EMAIL_RE.test(email.trim()));
    const customFieldsValid = validateCustomFields();
    if (!contactValid) {
      setError(labels.error_generic);
      return;
    }
    if (!customFieldsValid) return;
    setSubmitting(true);
    try {
      /* Idempotence : si un checkout a déjà réussi pour ces valeurs (retour depuis le paiement,
         échec transitoire), on NE recrée PAS de réservation. */
      let result = checkoutResult;
      if (!result) {
        if (pendingReleaseRef.current) {
          await pendingReleaseRef.current;
          pendingReleaseRef.current = null;
        }
        // Réponses aux SEULS champs applicables à la course (une réponse saisie pour une formule
        // abandonnée, ou restaurée pour un champ restreint à des lieux, n'est pas envoyée) — même
        // filtre que la pré-réservation du tunnel classique.
        const customFieldAnswers = serializeCustomFieldAnswers(answersForFields(customAnswers, visibleCustomFields));
        result = await callbacks.onCheckout({
          ...buildTripPayload(),
          contact: {
            firstname: firstname.trim(),
            lastname: lastname.trim(),
            email: email.trim(),
            ...(phone.trim() ? { phone_number: phone.trim() } : {}),
            ...(note.trim() ? { note: note.trim() } : {}),
          },
          // Empreinte du devis AFFICHÉ (garde de consentement) : si la recotation serveur du
          // checkout produit d'autres conditions (véhicule au tarif différent, config paiement
          // éditée…), le backend refuse en RIDE_QUOTE_CHANGED au lieu d'engager le client sur
          // un montant jamais affiché — géré dans le catch ci-dessous.
          ...(quote?.quote_fingerprint ? { expected_quote_fingerprint: quote.quote_fingerprint } : {}),
          ...(customFieldAnswers.length > 0 ? { custom_field_answers: customFieldAnswers } : {}),
        });
        setCheckoutResult(result);
      }
      // Sans paiement requis (politique FREE ou pro sans Stripe Connect) → succès direct.
      if (!result.requires_payment || !result.payment_uuid) setRequestConfirmed(true);
    } catch (e) {
      // Le devis affiché ne correspond plus à la recotation serveur (véhicule au tarif
      // différent, conditions éditées entre devis et checkout) : on invalide le devis et on
      // renvoie au formulaire trajet — le client recalcule, voit le nouveau prix et
      // re-consent. Jamais d'engagement sur un montant non affiché.
      if ((e as { errorKey?: string })?.errorKey === 'RIDE_QUOTE_CHANGED') {
        invalidateQuote();
        setStep('trip');
      }
      setError(resolveError(e));
    } finally {
      setSubmitting(false);
    }
  };

  // Politique FULL_PAYMENT_OPTIONAL : le client choisit « payer sur place » → on confirme la course
  // sans paiement en ligne (annule l'intent Stripe côté backend) et on bascule sur l'écran de succès.
  const handlePayOnSite = async (): Promise<void> => {
    if (!paymentUuid) return;
    setError(null);
    setPayingOnSite(true);
    try {
      await callbacks.onConfirmPayOnSite(paymentUuid);
      setRequestConfirmed(true);
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setPayingOnSite(false);
    }
  };

  /* Étape affichée : à l'étape devis sans devis (perdu), c'est le formulaire trajet qui s'affiche. */
  const view: Step = step === 'quote' && !quote ? 'trip' : step;
  const rootRef = useRef<HTMLDivElement | null>(null);
  useStepTransitionFocus(rootRef, view);
  /* Un seul message d'étape monté à la fois (trajet, devis ou coordonnées) : la même ref les sert tous. */
  const errorRef = useScrollToError(error);

  /* ── États hors étapes ─────────────────────────────────────────────── */

  // Reprise d'un paiement ouvert (retour arrière) : on attend son statut plutôt que d'afficher un
  // tunnel neuf qui changerait d'écran une fois le statut connu.
  if (loading || (resumingOpenPayment && config)) {
    return (
      <div className="bt-root">
        <div className="bt-loading">
          <div className="bt-spinner" />
        </div>
      </div>
    );
  }
  // Sans formule réservable, le tunnel est indisponible (défensif : features.can_ride masque
  // normalement l'onglet Trajet en amont — cache /public/professional 30 s).
  if (loadError || !config || config.prestations.length === 0) {
    return (
      <div className="bt-root">
        <div className="bt-step">
          <p className="bt-error">{labels.unavailable}</p>
        </div>
      </div>
    );
  }
  if (paid || requestConfirmed) {
    // Succès = pattern « manage-inline » du tunnel booking : la vue de gestion (annulation,
    // récap course, paiement, coordonnées pro) est LE récap — même composant que la page
    // /book/[slug]/manage/[uuid] pointée par l'email « Voir mon rendez-vous ».
    // Sablier ⏳ tant que la course n'est pas fermement confirmée : validation manuelle du pro
    // (pending_confirmation) OU demande sans paiement (« Demande envoyée », le chauffeur recontacte).
    // ✓ seulement pour une course payée ET auto-confirmée.
    const awaitingConfirmation = !(paid && !requiresProConfirmation);
    return (
      <div className="bt-root">
        <BookingOutcome
          awaitingConfirmation={awaitingConfirmation}
          title={
            paid && requiresProConfirmation
              ? labels.pending_confirmation_title
              : paid
                ? labels.success_title
                : labels.request_success_title
          }
          message={
            paid && requiresProConfirmation
              ? labels.pending_confirmation_subtitle
              : paid
                ? labels.success_subtitle
                : labels.request_success_subtitle
          }
        >
          <div className="ride-recap">
            {renderRouteMap &&
              pickup &&
              dropoff &&
              renderRouteMap({
                pickupLat: pickup.latitude,
                pickupLng: pickup.longitude,
                dropoffLat: dropoff.latitude,
                dropoffLng: dropoff.longitude,
                ...(quote?.route_polyline ? { encodedPolyline: quote.route_polyline } : {}),
              })}
            {reservationUuid && <BookingManageView uuid={reservationUuid} className="booking-manage-view--inline" />}
          </div>
        </BookingOutcome>
      </div>
    );
  }

  if (redirectReturn.isBlocking) {
    return (
      <div className="bt-root">
        <div className="bt-redirect-finalizing-view">{redirectNotice}</div>
      </div>
    );
  }

  const currentIndex = STEPS.indexOf(step);
  const paymentFormVisible = step === 'details' && paymentUuid != null;
  const showTrustBar =
    !paymentFormVisible &&
    !isFree &&
    config.allowed_payment_methods != null &&
    config.allowed_payment_methods.length > 0;

  const paymentNotice = ((): string | null => {
    if (!checkoutResult) return null;
    if (paymentType === PrestationPaymentType.DEPOSIT) {
      return labels.deposit_notice.replace('{amount}', checkoutResult.formatted_amount);
    }
    if (paymentType === PrestationPaymentType.BANK_IMPRINT) {
      return labels.imprint_notice.replace('{amount}', checkoutResult.formatted_amount);
    }
    if (paymentType === PrestationPaymentType.CARD_SAVE) return labels.card_save_notice;
    return null;
  })();

  /* Avertissement du pré-check consultatif (aucun véhicule libre sur ce créneau), affiché à
     CHAQUE étape jusqu'au paiement : dès qu'un devis existe, le pré-check tourne avec la VRAIE
     durée (pas les 60 min par défaut) — le montrer aux étapes devis/détails évite que le client
     découvre l'indispo seulement au checkout. Jamais bloquant (le checkout reste l'autorité). */
  const availabilityBanner = availabilityWarning ? (
    <p className="bt-availability-warning">{availabilityWarning}</p>
  ) : null;

  /* Bornes `min` des pickers natifs : grisent les dates passées dans le calendrier, avec la
     même référence « maintenant » (heure murale du PRO, cf. nowWallTimeInTimezone) que la
     validation au submit — qui reste l'autorité (un onglet resté ouvert a un `min` périmé,
     et `min` ne bloque pas une saisie clavier). Le retour est en plus borné à l'aller. */
  const minPickupDatetime = nowWallTimeInTimezone(config?.timezone ?? 'Europe/Paris');
  const minReturnDatetime = pickupDatetime || minPickupDatetime;

  /* ── Étape 1 : trajet (formulaire) ─────────────────────────────────── */
  const tripStep = (
    <div className="bt-step">
      <div className={`bt-step-header ${headerAccessory ? 'bt-step-header--row' : ''}`}>
        <div>
          <h2 className="bt-step-header__title">{labels.title}</h2>
          <p className="bt-step-header__subtitle">{professionalName}</p>
        </div>
        {headerAccessory ? <div className="bt-step-header__accessory">{headerAccessory}</div> : null}
      </div>

      <div className="bt-checkout">
        <div className="bt-checkout__section">
          {/* Type de trajet en tête du formulaire (convention des tunnels de transport : ce choix
              pilote l'apparition du champ date de retour plus bas). Segmented compact (classes
              bt-modes du TunnelModeSwitch), sans label visible — les deux libellés se suffisent —
              d'où l'aria-label de groupe. */}
          <div className="bt-modes bt-modes--field" role="group" aria-label={labels.trip_type}>
            {(['ONE_WAY', 'ROUND_TRIP'] as TripType[]).map((tt): JSX.Element => {
              const active = tripType === tt;
              const Icon = tt === 'ONE_WAY' ? ArrowRight : ArrowRightLeft;
              return (
                <button
                  key={tt}
                  type="button"
                  aria-pressed={active}
                  className={`bt-mode ${active ? 'bt-mode--active' : ''}`}
                  onClick={(): void => handleTripTypeChange(tt)}
                >
                  <Icon size={15} strokeWidth={2} aria-hidden="true" />
                  {tt === 'ONE_WAY' ? labels.trip_one_way : labels.trip_round_trip}
                </button>
              );
            })}
          </div>

          {/* Layout compact : grilles 2 colonnes (bt-checkout__fields--2col, empilées mobile)
              pour limiter le scroll de la première étape. */}
          <div className="bt-checkout__fields bt-checkout__fields--2col">
            <RideAddressField
              id="ride-pickup"
              label={labels.pickup_address}
              placeholder={labels.pickup_placeholder}
              searchHint={labels.address_search_hint}
              value={pickup}
              onChange={onTripField(setPickup)}
              onAutocomplete={callbacks.onAutocompleteAddress}
              onResolveDetails={callbacks.onResolveAddressDetails}
              validateSelection={pickupLocked ? undefined : validatePickupSelection}
              errorText={pickupZoneError ?? undefined}
              disabled={pickupLocked}
              lockedHint={pickupLocked ? labels.address_locked : undefined}
            />
            <RideAddressField
              id="ride-dropoff"
              label={labels.dropoff_address}
              placeholder={labels.dropoff_placeholder}
              searchHint={labels.address_search_hint}
              value={dropoff}
              onChange={onTripField(setDropoff)}
              onAutocomplete={callbacks.onAutocompleteAddress}
              onResolveDetails={callbacks.onResolveAddressDetails}
              disabled={dropoffLocked}
              lockedHint={dropoffLocked ? labels.address_locked : undefined}
            />
          </div>

          <div className="bt-checkout__fields bt-checkout__fields--2col">
            <div className="bt-field">
              <label className="bt-field__label" htmlFor="ride-pickup-datetime">
                {labels.pickup_datetime}
              </label>
              <input
                id="ride-pickup-datetime"
                type="datetime-local"
                className="bt-field__input"
                value={pickupDatetime}
                min={minPickupDatetime}
                onChange={(event): void => handlePickupDatetimeChange(event.target.value)}
                onBlur={handlePickupDatetimeBlur}
                onFocus={openNativeDatePicker}
                onClick={openNativeDatePicker}
                aria-invalid={!!pickupDateError}
              />
              {pickupDateError && (
                <p className="bt-error" role="alert">
                  {pickupDateError}
                </p>
              )}
            </div>
            {tripType === 'ROUND_TRIP' && (
              <div className="bt-field">
                <label className="bt-field__label" htmlFor="ride-return-datetime">
                  {labels.return_datetime}
                </label>
                <input
                  id="ride-return-datetime"
                  type="datetime-local"
                  className="bt-field__input"
                  value={returnDatetime}
                  min={minReturnDatetime}
                  onChange={(event): void => handleReturnDatetimeChange(event.target.value)}
                  onBlur={handleReturnDatetimeBlur}
                  onFocus={openNativeDatePicker}
                  onClick={openNativeDatePicker}
                  aria-invalid={!!returnDateError}
                />
                {returnDateError && (
                  <p className="bt-error" role="alert">
                    {returnDateError}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Pré-check consultatif : avertit qu'aucun véhicule n'est libre sur ce créneau,
              sans jamais bloquer (le checkout revalide sous verrou). */}
          {availabilityBanner}

          <div className="bt-checkout__fields">
            <QuantitySelector
              quantity={passengers}
              onChange={handlePassengersChange}
              className="bt-quantity--boxed"
              min={1}
              max={passengerCounterMax}
              labels={{
                quantity_label: labels.passengers,
                decrease_quantity: labels.decrease_quantity,
                increase_quantity: labels.increase_quantity,
              }}
            />
          </div>
          <div className="bt-checkout__fields bt-checkout__fields--2col">
            <QuantitySelector
              quantity={smallLuggage}
              onChange={(next): void => handleLuggageChange('small', next)}
              className="bt-quantity--boxed"
              min={0}
              max={smallLuggageCounterMax}
              labels={{
                quantity_label: labels.small_luggage,
                decrease_quantity: labels.decrease_quantity,
                increase_quantity: labels.increase_quantity,
              }}
            />
            <QuantitySelector
              quantity={largeLuggage}
              onChange={(next): void => handleLuggageChange('large', next)}
              className="bt-quantity--boxed"
              min={0}
              max={largeLuggageCounterMax}
              labels={{
                quantity_label: labels.large_luggage,
                decrease_quantity: labels.decrease_quantity,
                increase_quantity: labels.increase_quantity,
              }}
            />
          </div>

          {/* Refus capacité à l'incrément : le compteur n'a pas bougé, le message dit pourquoi. */}
          {capacityError?.anchor === 'quantity' && (
            <p className="bt-error" role="alert">
              {capacityError.message}
            </p>
          )}

          {/* Volet « Type de course » — trois rendus exclusifs :
              1. Vitrine (« Réserver ce trajet », mode véhicules) : chip de RAPPEL non modifiable
                 de la formule choisie — pas de sélecteur, pas de « peu importe ».
              2. Mode véhicules (config.vehicles présent, accès direct) : sélecteur de VÉHICULE —
                 « Peu importe » en tête (défaut) + une pill par véhicule (nom + capacité).
              3. Fallback (config.vehicles absent — anciens backends) : sélecteur de formules
                 historique inchangé (pill « Peu importe » dès ≥2 formules). */}
          {vitrineMode && selectedPrestation ? (
            <div className="bt-duration">
              <span className="bt-field__label">{labels.prestation}</span>
              <div className="ride-recap__rows ride-recap__rows--card">
                <div className="ride-recap__row">
                  <span className="ride-recap__row-icon" aria-hidden="true">
                    🚘
                  </span>
                  <span className="ride-recap__row-label">{labels.formula_reminder}</span>
                  <span className="ride-recap__row-value">{selectedPrestation.name}</span>
                </div>
              </div>
              {selectedPrestation.description ? (
                <RichDescription html={selectedPrestation.description} labels={labels} />
              ) : null}
            </div>
          ) : hasVehicles ? (
            <div className="bt-duration">
              <span className="bt-field__label">{labels.vehicle}</span>
              {/* Tuiles compactes (grille) : photo + nom + capacité tronqués ; le détail complet
                  (description, photos en grand) s'ouvre en modale via le bouton « i ».
                  - MONO : `radiogroup`, choix exclusif, navigable au clavier ;
                  - MULTI : `group` de sélecteurs de quantité (0..plafond) — plusieurs classes
                    peuvent être retenues, tout à 0 revient à « Automatique ». */}
              <div
                className={multiVehicleActive ? 'bt-vehicle-grid bt-vehicle-grid--multi' : 'bt-vehicle-grid'}
                role={multiVehicleActive ? 'group' : 'radiogroup'}
                aria-label={labels.vehicle}
              >
                <RideVehicleCard
                  key="any-vehicle"
                  name={multiVehicleActive ? labels.vehicle_auto : labels.any_vehicle}
                  selected={selection === 'any'}
                  onSelect={(): void => trySelect('any')}
                  labels={vehicleCardLabels}
                />
                {vehicles.map((vehicle): JSX.Element => {
                  const quantity = composition.find((i): boolean => i.vehicleId === vehicle.pro_resource_id)?.quantity;
                  return (
                    <RideVehicleCard
                      key={vehicle.pro_resource_id}
                      name={vehicle.name}
                      description={vehicle.description}
                      capacity={formatRideCapacityLabel(vehicle, {
                        passengers: labels.passengers,
                        small_luggage: labels.small_luggage,
                        large_luggage: labels.large_luggage,
                      })}
                      pictures={vehicle.pictures}
                      selected={
                        multiVehicleActive ? (quantity ?? 0) > 0 : selectedVehicleId === vehicle.pro_resource_id
                      }
                      onSelect={(): void =>
                        multiVehicleActive
                          ? handleVehicleQuantityChange(vehicle.pro_resource_id, Math.max(1, quantity ?? 0))
                          : trySelect({ vehicleId: vehicle.pro_resource_id })
                      }
                      labels={vehicleCardLabels}
                      {...(multiVehicleActive
                        ? {
                            quantity: {
                              value: quantity ?? 0,
                              // Plafond par classe = ce qu'il reste sous le plafond de la résa.
                              max: Math.max(quantity ?? 0, maxVehicles - compositionVehicleCount + (quantity ?? 0)),
                              onChange: (next: number): void =>
                                handleVehicleQuantityChange(vehicle.pro_resource_id, next),
                              labels: {
                                decrease: labels.decrease_quantity,
                                increase: labels.increase_quantity,
                              },
                            },
                          }
                        : {})}
                    />
                  );
                })}
              </div>
              {multiVehicleActive && <p className="bt-checkout__info-text">{labels.vehicle_auto_hint}</p>}
              {/* Refus : véhicule trop petit pour la saisie courante — la sélection n'a pas changé. */}
              {capacityError?.anchor === 'selection' && (
                <p className="bt-error" role="alert">
                  {capacityError.message}
                </p>
              )}
            </div>
          ) : (
            <div className="bt-duration">
              <span className="bt-field__label">{labels.prestation}</span>
              <div className="bt-duration__options bt-duration__options--fill">
                {ridePrestations.length >= 2 && (
                  <button
                    key="any-formula"
                    type="button"
                    className={`bt-duration__option ${selection === 'any' ? 'bt-duration__option--selected' : ''}`}
                    onClick={(): void => trySelect('any')}
                  >
                    <span className="bt-duration__option-duration">{labels.any_formula}</span>
                  </button>
                )}
                {ridePrestations.map((prestation): JSX.Element => {
                  const capacity = formatRideCapacityLabel(prestation, {
                    passengers: labels.passengers,
                    small_luggage: labels.small_luggage,
                    large_luggage: labels.large_luggage,
                  });
                  return (
                    <button
                      key={prestation.prestation_id}
                      type="button"
                      className={`bt-duration__option ${
                        selectionPrestationId === prestation.prestation_id ? 'bt-duration__option--selected' : ''
                      }`}
                      onClick={(): void => trySelect({ prestationId: prestation.prestation_id })}
                    >
                      <span className="bt-duration__option-duration">{prestation.name}</span>
                      {capacity && <span className="bt-duration__option-price">{capacity}</span>}
                    </button>
                  );
                })}
              </div>
              {/* Refus : formule trop petite pour la saisie courante — la sélection n'a pas changé. */}
              {capacityError?.anchor === 'selection' && (
                <p className="bt-error" role="alert">
                  {capacityError.message}
                </p>
              )}
              {selectedPrestation?.description ? (
                <RichDescription html={selectedPrestation.description} labels={labels} />
              ) : null}
            </div>
          )}
        </div>

        {error && (
          <p className="bt-error" role="alert" ref={errorRef}>
            {error}
          </p>
        )}
      </div>
    </div>
  );

  /* ── Étape 2 : estimation (carte + détail du prix) ─────────────────── */
  const quoteStep = (
    <div className="bt-step">
      <div className="bt-step-header">
        <h2 className="bt-step-header__title">{labels.step_quote_title}</h2>
        <p className="bt-step-header__subtitle">{professionalName}</p>
      </div>

      <div className="bt-checkout">
        {quote && pickup && dropoff && (
          <div className="bt-checkout__section">
            {/* Saisie du client en tête et en évidence (carte encadrée) — mêmes rangées
                icône/label/valeur que le récap de l'écran de succès (ride-recap__row). */}
            <div className="ride-recap__rows ride-recap__rows--card">
              <div className="ride-recap__row">
                <span className="ride-recap__row-icon" aria-hidden="true">
                  📍
                </span>
                <span className="ride-recap__row-label">{labels.pickup_label}</span>
                <span className="ride-recap__row-value">{pickup.formatted_address}</span>
              </div>
              <div className="ride-recap__row">
                <span className="ride-recap__row-icon" aria-hidden="true">
                  🏁
                </span>
                <span className="ride-recap__row-label">{labels.dropoff_label}</span>
                <span className="ride-recap__row-value">{dropoff.formatted_address}</span>
              </div>
              {pickupDatetime && (
                <div className="ride-recap__row">
                  <span className="ride-recap__row-icon" aria-hidden="true">
                    🕐
                  </span>
                  <span className="ride-recap__row-label">{labels.pickup_time_label}</span>
                  <span className="ride-recap__row-value">{formatLocalDatetime(pickupDatetime, locale)}</span>
                </div>
              )}
              {tripType === 'ROUND_TRIP' && returnDatetime && (
                <div className="ride-recap__row">
                  <span className="ride-recap__row-icon" aria-hidden="true">
                    ↩️
                  </span>
                  <span className="ride-recap__row-label">{labels.return_time_label}</span>
                  <span className="ride-recap__row-value">{formatLocalDatetime(returnDatetime, locale)}</span>
                </div>
              )}
              <div className="ride-recap__row">
                <span className="ride-recap__row-icon" aria-hidden="true">
                  📏
                </span>
                <span className="ride-recap__row-label">{labels.distance}</span>
                <span className="ride-recap__row-value">
                  {labels.distance_value.replace(
                    '{distance}',
                    quote.distance_km.toLocaleString(locale, { maximumFractionDigits: 1 }),
                  )}
                </span>
              </div>
              <div className="ride-recap__row">
                <span className="ride-recap__row-icon" aria-hidden="true">
                  ⏱️
                </span>
                <span className="ride-recap__row-label">{labels.duration}</span>
                <span className="ride-recap__row-value">{quote.formatted_duration}</span>
              </div>
              <div className="ride-recap__row">
                <span className="ride-recap__row-icon" aria-hidden="true">
                  👥
                </span>
                <span className="ride-recap__row-label">{labels.passengers}</span>
                <span className="ride-recap__row-value">
                  {passengers}
                  {formatLuggageCounts(labels, luggage, smallLuggage, largeLuggage)}
                </span>
              </div>
              {vehicleLines.length > 0 ? (
                /* Devis multi-véhicules : une ligne PAR véhicule (classe, part du groupe, prix
                   de CE véhicule — chacun est facturé en entier, sans remise de groupe), sous un
                   en-tête « Véhicules (N) » qui porte le libellé de composition du backend. */
                <>
                  <div className="ride-recap__row">
                    <span className="ride-recap__row-icon" aria-hidden="true">
                      🚘
                    </span>
                    <span className="ride-recap__row-label">
                      {labels.vehicles_title.replace('{count}', String(vehicleLines.length))}
                    </span>
                    <span className="ride-recap__row-value">{quote.vehicle_name ?? ''}</span>
                  </div>
                  {vehicleLines.map(
                    (line): JSX.Element => (
                      <div className="ride-recap__row" key={line.position}>
                        <span className="ride-recap__row-icon" aria-hidden="true">
                          👥
                        </span>
                        <span className="ride-recap__row-label">
                          {`${line.vehicle_name} · ${line.passenger_count} ${labels.passengers.toLowerCase()}`}
                          {formatLuggageCounts(
                            labels,
                            line.luggage_count,
                            line.small_luggage_count,
                            line.large_luggage_count,
                          )}
                        </span>
                        {/* Prix formaté par le backend (devise + locale) — jamais recalculé ici. */}
                        <span className="ride-recap__row-value">{line.formatted_price}</span>
                      </div>
                    ),
                  )}
                </>
              ) : (
                <div className="ride-recap__row">
                  <span className="ride-recap__row-icon" aria-hidden="true">
                    🚘
                  </span>
                  {/* Mode véhicules (hors vitrine) : la ligne parle du VÉHICULE ; sinon de la
                      formule (le véhicule précis n'est affecté qu'au checkout). */}
                  <span className="ride-recap__row-label">
                    {hasVehicles && selectedPrestation == null ? labels.vehicle : labels.prestation}
                  </span>
                  <span className="ride-recap__row-value">
                    {/* Formule choisie, sinon véhicule choisi, sinon véhicule retenu par le devis
                        (« peu importe » : le backend a affecté le moins cher disponible). */}
                    {selectedPrestation?.name ?? selectedVehicle?.name ?? quote.vehicle_name ?? ''}
                  </span>
                </div>
              )}
              {/* Décomposition affichée seulement quand le total ne se réduit pas au trajet
                  (frais d'attente/d'approche) : sans elle, un A/R avec attente est inexplicable.
                  Multi-véhicules : repliée derrière « Voir le détail » pour que la liste des
                  véhicules reste lisible — la décomposition porte alors sur le TOTAL. */}
              {(quote.formatted_waiting_fees || quote.formatted_approach_fees) && (
                <>
                  {vehicleLines.length > 0 && (
                    <button
                      type="button"
                      className="bt-link-subtle"
                      aria-expanded={breakdownExpanded}
                      onClick={(): void => setBreakdownExpanded((open): boolean => !open)}
                    >
                      {breakdownExpanded ? labels.hide_detail : labels.show_detail}
                    </button>
                  )}
                  {(vehicleLines.length === 0 || breakdownExpanded) && (
                    <>
                      <div className="ride-recap__row">
                        <span className="ride-recap__row-icon" aria-hidden="true">
                          🛣️
                        </span>
                        <span className="ride-recap__row-label">{labels.route_price}</span>
                        <span className="ride-recap__row-value">{quote.formatted_route_price}</span>
                      </div>
                      {quote.formatted_approach_fees && (
                        <div className="ride-recap__row">
                          <span className="ride-recap__row-icon" aria-hidden="true">
                            🚗
                          </span>
                          <span className="ride-recap__row-label">{labels.approach_fees}</span>
                          <span className="ride-recap__row-value">{quote.formatted_approach_fees}</span>
                        </div>
                      )}
                      {quote.formatted_handling_fees && (
                        <div className="ride-recap__row">
                          <span className="ride-recap__row-icon" aria-hidden="true">
                            🧾
                          </span>
                          <span className="ride-recap__row-label">{labels.handling_fees}</span>
                          <span className="ride-recap__row-value">{quote.formatted_handling_fees}</span>
                        </div>
                      )}
                      {quote.formatted_waiting_fees && (
                        <div className="ride-recap__row">
                          <span className="ride-recap__row-icon" aria-hidden="true">
                            ⏳
                          </span>
                          <span className="ride-recap__row-label">{labels.waiting_fees}</span>
                          <span className="ride-recap__row-value">{quote.formatted_waiting_fees}</span>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
              <div className="ride-recap__row ride-recap__row--total">
                <span className="ride-recap__row-icon" aria-hidden="true">
                  💶
                </span>
                <span className="ride-recap__row-label">{labels.estimated_price}</span>
                <span className="ride-recap__row-value">{quote.formatted_price}</span>
              </div>
            </div>

            {/* Mini-carte optionnelle : rendue seulement si l'hôte fournit un loader Google Maps
                (frontend-web). Les sites publics (booking-widget) n'en ont pas → on n'affiche rien
                plutôt qu'un message « carte indisponible » trompeur. */}
            {renderRouteMap
              ? renderRouteMap({
                  pickupLat: pickup.latitude,
                  pickupLng: pickup.longitude,
                  dropoffLat: dropoff.latitude,
                  dropoffLng: dropoff.longitude,
                  ...(quote.route_polyline ? { encodedPolyline: quote.route_polyline } : {}),
                })
              : null}
          </div>
        )}

        {/* Verdict de dispo recalculé avec la vraie durée du devis : montré ICI (avant contact
            + paiement) pour ne pas découvrir l'indispo seulement au checkout. */}
        {availabilityBanner}

        {error && (
          <p className="bt-error" role="alert" ref={errorRef}>
            {error}
          </p>
        )}
      </div>
    </div>
  );

  /* ── Étape 3 : coordonnées → paiement inline ───────────────────────── */
  const detailsStep = (
    <div className="bt-step">
      <div className="bt-step-header">
        <h2 className="bt-step-header__title">{paymentUuid ? labels.step_payment_title : labels.contact_title}</h2>
        <p className="bt-step-header__subtitle">{professionalName}</p>
      </div>

      <div className="bt-checkout">
        {!paymentUuid ? (
          <>
            <div className="bt-checkout__section">
              <CustomerInfoFields
                idPrefix="ride"
                values={{ firstname, lastname, email, phone }}
                phoneRequired={false}
                locale={locale}
                flagBaseUrl={flagBaseUrl}
                labels={{
                  firstname: labels.firstname,
                  lastname: labels.lastname,
                  email: labels.email,
                  phone: labels.phone,
                }}
                onChange={(field, value): void => {
                  if (field === 'firstname') setFirstname(value);
                  else if (field === 'lastname') setLastname(value);
                  else if (field === 'email') setEmail(value);
                  else setPhone(value);
                  invalidateCheckout();
                }}
              />
              <FormField
                id="ride-note"
                label={labels.note}
                type="textarea"
                value={note}
                onChange={(v): void => {
                  setNote(String(v));
                  invalidateCheckout();
                }}
              />
            </div>

            {/* Questionnaire du pro (champs personnalisés) — même composant et même rendu que le
                tunnel classique ; rien n'est rendu sans champ applicable à la course. Toute réponse
                modifiée invalide le checkout (elle fait partie de sa charge utile). */}
            <DynamicFormFields
              fields={visibleCustomFields}
              answers={customAnswers}
              onUpdateAnswer={(fieldId, value): void => {
                updateCustomAnswer(fieldId, value);
                invalidateCheckout();
              }}
              errors={customErrors}
              onBlurField={blurCustomField}
              sectionLabel={labels.additional_info}
            />

            {/* Modalités de paiement/remboursement — MÊME timeline que le tunnel booking classique,
                sous le formulaire d'informations. */}
            <PaymentTimeline
              paymentType={paymentType}
              paymentInfos={quote?.payment_infos}
              cancellationDeadlineIso={quote?.cancellation_deadline}
              cancellationDeadlineFormatted={quote?.cancellation_deadline_formatted}
              labels={labels}
            />

            {/* Dernier rappel de dispo (vraie durée) avant de valider — non bloquant. */}
            {availabilityBanner}

            {error && (
              <p className="bt-error" role="alert" ref={errorRef}>
                {error}
              </p>
            )}
          </>
        ) : (
          <>
            {redirectNotice}
            {paymentNotice && <p className="bt-checkout__info-text">{paymentNotice}</p>}
            <GiftCardPaymentSection
              uuid={paymentUuid}
              redirectReturnParam={REDIRECT_RETURN_PARAM.RIDE}
              callbacks={callbacks}
              stripePublishableKey={stripePublishableKey}
              allowedPaymentMethods={config.allowed_payment_methods}
              labels={{
                pay: labels.pay,
                secure_payment: labels.secure_payment,
                payment_methods_accepted: labels.payment_methods_accepted,
                error_generic: labels.error_generic,
                finalizing: labels.finalizing,
                verifying: labels.verifying,
                retry: labels.retry,
              }}
              onBusyChange={setPaymentBusy}
              onPaid={(result): void => {
                // Paiement repris après une redirection : la course n'est connue que par ce statut.
                setReturnedPayment((current): { paymentUuid: string; reservationUuid?: string } | null =>
                  current
                    ? { ...current, reservationUuid: current.reservationUuid ?? result.reservation_uuid }
                    : current,
                );
                setPaid(true);
              }}
            />
            {/* Paiement intégral OPTIONNEL : alternative « payer sur place » à côté du paiement en ligne. */}
            {isPayOnSite && (
              <>
                <p className="bt-checkout__info-text">{labels.pay_on_site_or_online}</p>
                <button
                  type="button"
                  className="bt-cta bt-cta--secondary"
                  disabled={payingOnSite}
                  onClick={(): void => void handlePayOnSite()}
                >
                  {payingOnSite ? <span className="bt-spinner bt-spinner--inline" /> : labels.pay_on_site}
                </button>
              </>
            )}
            {/* Retour au formulaire contact pour corriger une info erronée (ex. email) : on invalide le
                checkout (paymentUuid retombe à null) et on annule la course en attente — elle bloque son
                véhicule et son chauffeur jusqu'à expiration (10 min). Un re-checkout crée une nouvelle
                réservation une fois l'annulation aboutie (voir releasePendingRide). */}
            {!paymentLocked && (
              <button type="button" className="bt-link-subtle bt-link-subtle--mt" onClick={invalidateCheckout}>
                {labels.edit_contact_info}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );

  /* ── Récapitulatif latéral ─────────────────────────────────────────── */
  /* Trajet saisi par le client — sous-lignes communes aux deux rendus du récapitulatif. */
  const summaryTripSublines = (): string[] =>
    pickup && dropoff
      ? [
          `📍 ${pickup.formatted_address}`,
          `🏁 ${dropoff.formatted_address}`,
          ...(pickupDatetime ? [`🕐 ${formatLocalDatetime(pickupDatetime, locale)}`] : []),
          ...(tripType === 'ROUND_TRIP' && returnDatetime ? [`↩️ ${formatLocalDatetime(returnDatetime, locale)}`] : []),
        ]
      : [];
  const summaryGroupSubline = (
    passengerCount: number,
    luggageCount: number,
    smallCount?: number | null,
    largeCount?: number | null,
  ): string =>
    `👥 ${passengerCount} ${labels.passengers.toLowerCase()}${formatLuggageCounts(labels, luggageCount, smallCount, largeCount)}`;

  const summaryItems: TunnelSummaryItem[] =
    quote && pickup && dropoff
      ? vehicleLines.length > 0
        ? // Multi-véhicules : un item PAR véhicule (chacun avec son prix, facturé en entier). Le
          // trajet, commun à toute la réservation, passe en lignes de contexte (summaryContextRows) :
          // en item, il gonflait le compteur de la barre mobile (« 3 éléments » pour 2 véhicules).
          vehicleLines.map(
            (line): TunnelSummaryItem => ({
              label: line.vehicle_name,
              price: line.formatted_price,
              sublines: [
                summaryGroupSubline(
                  line.passenger_count,
                  line.luggage_count ?? 0,
                  line.small_luggage_count,
                  line.large_luggage_count,
                ),
              ],
              supplements: [],
            }),
          )
        : [
            {
              label: quote.vehicle_name ?? labels.summary_ride_label,
              price: quote.formatted_price,
              sublines: [
                ...summaryTripSublines(),
                summaryGroupSubline(passengers, luggage, smallLuggage, largeLuggage),
              ],
              supplements: [],
            },
          ]
      : [];

  // Trajet du récapitulatif multi-véhicules (en mono, il reste dans les sous-lignes de l'item unique).
  const summaryContextRows: TunnelSummaryContextRow[] =
    quote && pickup && dropoff && vehicleLines.length > 0
      ? summaryTripSublines().map((text): TunnelSummaryContextRow => ({ text }))
      : [];

  const paymentRows = ((): TunnelSummaryPaymentRow[] | undefined => {
    if (!checkoutResult?.requires_payment) return undefined;
    return [{ label: labels.total_label, value: checkoutResult.formatted_amount, highlight: true }];
  })();

  /* Récap : mêmes lignes pour la sidebar (layout wide) et la feuille de la barre du bas. */
  const summaryProps: Omit<TunnelSummaryProps, 'variant'> = {
    items: summaryItems,
    contextRows: summaryContextRows,
    totalValue: quote?.formatted_price ?? '',
    totalDuration: quote?.formatted_duration,
    paymentRows,
    labels: { selection_title: labels.selection_title, total_label: labels.total_label },
  };

  /* Actions de l'étape affichée, rendues dans la sidebar (wide) ou la barre du bas (colonne
     unique). Paiement ouvert : la section Stripe porte son bouton, et le retour n'est plus proposé. */
  const primary = ((): TunnelSummaryCta | undefined => {
    if (view === 'trip')
      return { label: labels.compute_price, onClick: (): void => void handleComputePrice(), loading: isComputing };
    if (view === 'quote') return { label: labels.continue, onClick: goToDetails, disabled: !quote };
    if (paymentUuid) return undefined;
    return {
      label: isFree ? labels.request_ride : labels.go_to_payment,
      onClick: (): void => void handleCheckout(),
      loading: submitting,
    };
  })();
  const onBack = view === 'quote' || (view === 'details' && !paymentUuid) ? back : undefined;

  return (
    <div className="bt-root" ref={rootRef}>
      <RideProgress currentIndex={currentIndex} total={STEPS.length} />
      {/* Course expirée pendant la redirection : le bloc paiement a disparu, le bandeau reste en tête. */}
      {redirectReturn.phase === 'expired' && redirectNotice}

      {showTrustBar && (
        <div className="bt-trust-bar">
          <PaymentReassurance
            layout="inline"
            methods={config.allowed_payment_methods}
            secureLabel={labels.secure_payment}
            methodsLabel={labels.payment_methods_accepted}
          />
        </div>
      )}

      <div className="bt-layout">
        <div className="bt-main">
          {view === 'trip' && tripStep}
          {view === 'quote' && quoteStep}
          {view === 'details' && detailsStep}
        </div>

        <TunnelSummary
          {...summaryProps}
          variant="sidebar"
          cta={primary}
          backAction={onBack ? { label: labels.back, onClick: onBack } : undefined}
        />
      </div>

      <TunnelFooterBar
        onBack={onBack}
        primary={primary}
        totalValue={summaryProps.totalValue}
        count={summaryItems.length}
        sheetTitle={labels.selection_title}
        sheet={<TunnelSummary {...summaryProps} variant="sheet" />}
        stepKey={paymentUuid ? 'payment' : view}
        labels={{
          back: labels.back,
          close: labels.close,
          show_detail: labels.show_detail,
          hide_detail: labels.hide_detail,
          item_singular: labels.item_singular,
          item_plural: labels.item_plural,
        }}
      />
    </div>
  );
}
